import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ClipboardList } from 'lucide-react';

import {
  Navbar,
  Container,
  Text,
  ChatInput,
  ChatMessage,
  ModeToggleSwitch,
  Icon,
  Button,
  RequirementGroups,
  DesignSystemPanel,
  ArtifactSharePannel,
  ModelPreference,
  InteractionMode,
  ReadOnlyProjectNotice,
} from '@ui-library';
import { DEFAULT_AGENT_SETTINGS } from '@ui-library/AgentSettingsMenu';
import { IconName } from '@ui-library/Icon/Icon.type';
import { ChatMode } from '@ui-library/ModeToggleSwitch/ModeToggleSwitch.types';
import type { RequirementGroupsRef } from '@ui-library/RequirementGroups/RequirementGroups.types';

import api, { getApiBaseUrl, authenticatedFetch } from '../utils/axios';
import { KnowledgeDocSharePannel } from '../components/KnowledgeDocSharePannel/KnowledgeDocSharePannel';
import { KnowledgeDocRecord } from '../services/knowledgeDocsService';
import { TestRecordingSharePanel } from '../components/TestRecordingSharePanel/TestRecordingSharePanel';
import { getRecordingDetails, updateRecordingShareState } from '../services/testRecordingService';
import type { TestRecordingShareInfo } from '../components/TestRecordingViewer/TestRecordingViewer';
import RollbackMarker from '../components/VersionControl/RollbackMarker';
import { useAuth } from '../contexts/AuthContext';
import { formatCostValue } from '../utils/formatCost';
import { formatLatency } from '../utils/formatLatency';
import { showDuration, canShowCost } from '../utils/usageConfig';
import { formatTimestamp, generateThreadTitle } from '../utils/dateUtils';
import { generateMessageId } from '../utils/messageIdGenerator';
import { useDevAgent } from '../hooks/useDevAgent';
import { useTicketTagChips } from '../hooks/useTicketTagChips';
import { usePromptQueue } from '../hooks/usePromptQueue';
import { useBrainstormWebSocket } from '../hooks/useBrainstormWebSocket';
import type { BrainstormWebSocketMessage } from '../services/brainstormWebsocketService';
import { IfHasPermission } from '../components/IfHasPermission';
import { Permissions, WELCOME_MESSAGE_ID, WELCOME_MESSAGE_TAP, WELCOME_MESSAGE_WEB } from '../constants';
import ApplicationUrlsModal from '../components/ApplicationUrlsModal';
import AuthCredentialsModal from '../components/AuthCredentialsModal';
import MCPServersModal from '../components/MCPServersModal';
import EnvironmentsPanel from '../components/EnvironmentsPanel';
import CredentialCaptureModal from '../components/CredentialCaptureModal';
import TestMetadataModal from '../components/TestMetadataModal';
import { ManageCollaborationModal } from '../components/ProjectModals';
import { DocumentsPanel } from '../components/DocumentsPanel';
import { CodebasePanel } from '../components/CodebasePanel';
import PreviewPanel from '../components/PreviewPanel/PreviewPanel';
import ArtifactDetail from '../components/ArtifactDetail';
import { KnowledgeDocDetail } from '../components/KnowledgeDocDetail/KnowledgeDocDetail';
import { KnowledgeDocPanelData } from '../components/KnowledgeDocDetail/KnowledgeDocDetail.type';
import { DatabasePreviewPannel } from '../components/DatabasePreviewPannel';
import { AppLifecycleControl } from '../components/AppLifecycle';
import { useTheme } from '../contexts/ThemeContext';
import { DevProject, Message, Attachment } from '../types/chat';
import type { Artifact } from '../utils/parseArtifacts';
import { saveProjectPosition, getProjectPosition } from '../utils/projectNavigationStorage';
import { isUrlPreviewBlocked } from '../utils/previewUtils';
import CABPage from './CABPage';
import TicketsPage from './TicketsPage';
import { PlatformType } from '../types/project';
import { QueuedMessageBanner } from '../components/chat/QueuedMessageBanner';
import { ChatLoadingIndicator } from '../components/chat/ChatLoadingIndicator';
import type { QueueJob } from '../types/queue';
import { QueueStatus } from '../types/queue';
import { serializeTicketTagsIntoContent } from '../utils/cabTagSerializer';
import { ChatNavigator, useChatIndex, useChatWindow } from '../components/ChatNavigator';
import { versionControlApi } from '../services/versionControlApi';

// DOM contract shared with the conversation navigator: each rendered message
// is wrapped in an element with id="message-<id>"
const getMessageElement = (id: string) => document.getElementById(`message-${id}`);

// Skeleton Loading Component for Chat Messages
const ChatMessageSkeleton: React.FC<{ index: number; type: 'user' | 'assistant' }> = ({ index, type }) => {
  return (
    <div
      className={`flex ${type === 'user' ? 'justify-end' : 'justify-start'} animate-fade-in-up opacity-0`}
      style={{
        animationDelay: `${index * 0.15}s`,
        animationFillMode: 'forwards'
      }}
    >
      {/* Always keep avatar on left, message on right */}
      <div className="flex gap-bg-v2-md flex-row w-[60%]">
        {/* Avatar Skeleton - Always on left */}
        <div className="w-8 h-8 rounded-full skeleton-shimmer flex-shrink-0"></div>

        {/* Message Content Skeleton */}
        <div className="flex flex-col gap-bg-v2-sm flex-1">
          {/* Message Lines - No box, just plain skeleton lines */}
          <div className="space-y-2">
            <div className="h-4 skeleton-shimmer rounded w-full"></div>
            <div className="h-4 skeleton-shimmer rounded w-full"></div>
            <div className="h-4 skeleton-shimmer rounded w-[95%]"></div>
            <div className="h-4 skeleton-shimmer rounded w-[90%]"></div>
          </div>

          {/* Timestamp Skeleton - Always on left */}
          <div className="h-3 skeleton-shimmer rounded w-32 self-start"></div>
        </div>
      </div>
    </div>
  );
};

export const ChatPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, token, hasAdminRole, isGuest } = useAuth();
  const showCost = canShowCost(hasAdminRole);
  const { projectId, type, threadId: urlThreadId } = useParams<{ projectId: string, type: string, threadId: string }>();

  const chatMode = ChatMode[type?.toUpperCase() as keyof typeof ChatMode] || ChatMode.CAB;
  const { theme, setTheme } = useTheme();
  const [mode, setMode] = useState<ChatMode>(chatMode);
  const [message, setMessage] = useState('');
  const [isSendingMessage, setIsSendingMessage] = useState(false);
  // Build-mode ticket / epic @-references (mirrors the CAB chat tag feature).
  const {
    selectedTicketTags,
    setSelectedTicketTags,
    ticketTagSuggestions,
    isTicketTagSearching,
    handleTicketTagSearch,
    handleTicketTagsChange,
  } = useTicketTagChips(projectId, token);
  const [allThreads, setAllThreads] = useState<any[]>([]);
  const [projectCost, setProjectCost] = useState<number>(0);
  const [projectDuration, setProjectDuration] = useState<number>(0);
  const [loadingProject, setLoadingProject] = useState(false);
  const [currentProject, setCurrentProject] = useState<DevProject | null>(null);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [model, setModelState] = useState<ModelPreference>(() => {
    if (!projectId) return ModelPreference.MEDIUM;
    
    const storageKey = `model_preference_${projectId}`;
    const stored = localStorage.getItem(storageKey);
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        const modeKey = chatMode === ChatMode.DESIGN ? 'design' : 'build';
        return parsed[modeKey] || ModelPreference.MEDIUM;
      } catch {
        return ModelPreference.MEDIUM;
      }
    }
    return ModelPreference.MEDIUM;
  });

  const setModel = (newModel: ModelPreference) => {
    if (!projectId) return;
    
    setModelState(newModel);
    const storageKey = `model_preference_${projectId}`;
    const stored = localStorage.getItem(storageKey);
    let preferences = { build: ModelPreference.MEDIUM, design: ModelPreference.MEDIUM };
    if (stored) {
      try {
        preferences = JSON.parse(stored);
      } catch {}
    }
    const modeKey = mode === ChatMode.DESIGN ? 'design' : 'build';
    preferences[modeKey] = newModel;
    localStorage.setItem(storageKey, JSON.stringify(preferences));
  };

  // Per-project agent settings (persisted to localStorage)
  const [agentSettings, setAgentSettingsState] = useState<Record<string, boolean>>(() => {
    if (!projectId) return DEFAULT_AGENT_SETTINGS;
    const stored = localStorage.getItem(`agent_settings_${projectId}`);
    if (stored) {
      try { return { ...DEFAULT_AGENT_SETTINGS, ...JSON.parse(stored) }; } catch {}
    }
    return DEFAULT_AGENT_SETTINGS;
  });

  const setAgentSettings = (newSettings: Record<string, boolean>) => {
    if (!projectId) return;
    setAgentSettingsState(newSettings);
    localStorage.setItem(`agent_settings_${projectId}`, JSON.stringify(newSettings));
  };

  // Build-mode interaction: Agent (build) vs Ask (read-only Q&A), persisted per project
  const [interactionMode, setInteractionModeState] = useState<InteractionMode>(() => {
    if (!projectId) return InteractionMode.AGENT;
    const stored = localStorage.getItem(`interaction_mode_${projectId}`);
    return stored === InteractionMode.ASK ? InteractionMode.ASK : InteractionMode.AGENT;
  });

  const setInteractionMode = (newMode: InteractionMode) => {
    if (!projectId) return;
    setInteractionModeState(newMode);
    localStorage.setItem(`interaction_mode_${projectId}`, newMode);
  };

  // Read-only members (no update permission) can only use Ask mode
  const canEditProject = !!currentProject?.permissions?.includes(Permissions.UpdateProjectPermission);
  useEffect(() => {
    if (currentProject && !canEditProject && interactionMode !== InteractionMode.ASK) {
      setInteractionMode(InteractionMode.ASK);
    }
  }, [currentProject, canEditProject]);

  // Modal states
  const [showUrlsModal, setShowUrlsModal] = useState(false);
  const [showCredentialsModal, setShowCredentialsModal] = useState(false);
  const [showMCPServersModal, setShowMCPServersModal] = useState(false);
  const [showEnvironmentsPanel, setShowEnvironmentsPanel] = useState(false);
  // Same gate as custom domains: v1 projects bake absolute URLs into their
  // frontend .env, so a copy of one calls the original app's API instead of its
  // own. The backend refuses to create environments for them — hiding the entry
  // point stops a user finding that out only after filling in the form.
  const supportsEnvironments = (currentProject?.prompt_version || 0) >= 2;
  const [showTestMetadataModal, setShowTestMetadataModal] = useState(false);
  const [showDocumentsPanel, setShowDocumentsPanel] = useState(false);
  const [showCodebasePanel, setShowCodebasePanel] = useState(false);
  const [showDesignSystemPanel, setShowDesignSystemPanel] = useState(false);
  const [showPreviewPanel, setShowPreviewPanel] = useState(false);
  const [showDatabasePanel, setShowDatabasePanel] = useState(false);
  const [initialCodebaseFile, setInitialCodebaseFile] = useState<string | null>(null);
  const [showManageCollaborationModal, setShowManageCollaborationModal] = useState(false);

  // Panel closing animation states
  const [isDocumentsPanelClosing, setIsDocumentsPanelClosing] = useState(false);
  const [isCodebasePanelClosing, setIsCodebasePanelClosing] = useState(false);
  const [isArtifactPanelClosing, setIsArtifactPanelClosing] = useState(false);
  const [isDesignSystemPanelClosing, setIsDesignSystemPanelClosing] = useState(false);
  const [isPreviewPanelClosing, setIsPreviewPanelClosing] = useState(false);
  const [isDatabasePanelClosing, setIsDatabasePanelClosing] = useState(false);
  const [isArtifactSharePannelClosing, setIsArtifactSharePannelClosing] = useState(false);
  
  // Artifact preview state
  const [selectedArtifact, setSelectedArtifact] = useState<Artifact | null>(null);
  const [selectedArtifactToShare, setSelectedArtifactToShare] = useState<Artifact | null>(null);
  const [showArtifactSharePannel, setShowArtifactSharePannel] = useState(false);

  // Knowledge document preview (inline side panel) state
  const [selectedKnowledgeDoc, setSelectedKnowledgeDoc] = useState<KnowledgeDocPanelData | null>(null);
  const [isKnowledgeDocPanelClosing, setIsKnowledgeDocPanelClosing] = useState(false);

  // Knowledge document share state
  const [selectedKnowledgeDocToShare, setSelectedKnowledgeDocToShare] = useState<KnowledgeDocRecord | null>(null);
  const [showKnowledgeDocSharePannel, setShowKnowledgeDocSharePannel] = useState(false);
  const [isKnowledgeDocSharePannelClosing, setIsKnowledgeDocSharePannelClosing] = useState(false);

  // Test recording share state
  const [testRecordingToShare, setTestRecordingToShare] = useState<(TestRecordingShareInfo & { initialEnabled: boolean }) | null>(null);
  const [showTestRecordingSharePannel, setShowTestRecordingSharePannel] = useState(false);
  const [isTestRecordingSharePannelClosing, setIsTestRecordingSharePannelClosing] = useState(false);

  // Version control revert state
  const [revertingMessageId, setRevertingMessageId] = useState<string | null>(null);

  // Smart auto-scroll state
  const [hasUserScrolledUp, setHasUserScrolledUp] = useState(false);
  const [isAtBottom, setIsAtBottom] = useState(true);
  const [devIsLoadingMore, setDevIsLoadingMore] = useState(false);
  // Downward pagination (loading newer messages below a historical window)
  const [devIsLoadingNewer, setDevIsLoadingNewer] = useState(false);

  // Requirement Folders ref
  const requirementGroupsRef = useRef<RequirementGroupsRef>(null);

  // Listen for artifact saved events to reload requirement folders
  useEffect(() => {
    const handleArtifactSaved = () => {
      console.log('🎉 Artifact saved event received, reloading requirement folders');
      requirementGroupsRef.current?.reloadCurrentGroupArtifacts();
    };

    window.addEventListener('artifactSaved', handleArtifactSaved);
    return () => {
      window.removeEventListener('artifactSaved', handleArtifactSaved);
    };
  }, []);


  // Validate chat mode - redirect if invalid
  useEffect(() => {
    if (type && !['design', 'build', 'cab', 'tickets'].includes(type.toLowerCase())) {
      console.warn(`Invalid chat mode: ${type}, redirecting to project page`);
      navigate(`/chat/${projectId}`);
    }
  }, [type, projectId, navigate]);

  // Redirect to /cab if type is missing
  useEffect(() => {
    if (projectId && !type) {
      navigate(`/chat/${projectId}/cab`, { replace: true });
    }
  }, [projectId, type, navigate]);

  // Sync mode state when URL changes (for browser back/forward and direct URL entry)
  useEffect(() => {
    if (type) {
      const urlMode = ChatMode[type.toUpperCase() as keyof typeof ChatMode];
      if (urlMode && urlMode !== mode) {
        setMode(urlMode);
      }
    }
  }, [type]);

  // Save mode to localStorage when navigating directly to /build or /cab as design is handled using handleThreadSelect
  useEffect(() => {
    if (projectId && type) {
      const lowerType = type.toLowerCase();
      if (lowerType === ChatMode.BUILD) {
        saveProjectPosition(projectId, ChatMode.BUILD, null);
      } else if (lowerType === ChatMode.CAB) {
        saveProjectPosition(projectId, ChatMode.CAB, null);
      }
    }
  }, [projectId, type, urlThreadId]);

  // Brainstorm (Design Mode) state
  const [brainstormThreadId, setBrainstormThreadId] = useState<string | undefined>(); 
  const [brainstormMessages, setBrainstormMessages] = useState<Message[]>([]);
  const [brainstormIsLoading, setBrainstormIsLoading] = useState(false);
  const [brainstormIsStreaming, setBrainstormIsStreaming] = useState(false);
  const [brainstormAbortController, setBrainstormAbortController] = useState<AbortController | null>(null);
  const [brainstormHasMoreMessages, setBrainstormHasMoreMessages] = useState(false);
  const [brainstormIsLoadingMore, setBrainstormIsLoadingMore] = useState(false);

  // Ref to track brainstormIsStreaming for avoiding closure issues in callbacks
  const brainstormIsStreamingRef = useRef(brainstormIsStreaming);
  useEffect(() => {
    brainstormIsStreamingRef.current = brainstormIsStreaming;
  }, [brainstormIsStreaming]);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const lastScrolledMessageIdRef = useRef<string | null>(null);
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const isLoadingRef = useRef<boolean>(false);
  const selectedArtifactIdRef = useRef<string | null>(null);

  // Track preview panel refresh key to force reload
  const [previewRefreshKey, setPreviewRefreshKey] = useState(0);
  
  // Use ref to track current showPreviewPanel state to avoid closure issues
  const showPreviewPanelRef = useRef(showPreviewPanel);
  useEffect(() => {
    showPreviewPanelRef.current = showPreviewPanel;
  }, [showPreviewPanel]);

  // Always-current pointer to promptQueue.refresh (declared before useDevAgent
  // so the WS callback below can reach it; assigned after usePromptQueue runs)
  const queueRefreshRef = useRef<(() => void) | null>(null);

  // Initialize devAgent hook first before using it in callbacks
  const devAgent = useDevAgent({
    projectId: projectId || '',
    onMessageUpdate: (messages) => {
      // Just update messages, no complex logic here
    },
    onRawWebSocketMessage: (message) => {
      // Queue events must trigger a refresh DIRECTLY: lastWsMessage is a
      // single slot, and during active streaming the next dev_phase_update /
      // log event can overwrite a queue event before the usePromptQueue
      // effect ever observes it — the banner then never refreshes.
      if (
        message?.type === 'queue_position_update' ||
        message?.type === 'queue_job_started' ||
        message?.type === 'queue_job_completed'
      ) {
        queueRefreshRef.current?.();
      }
      // Pass raw websocket events to prompt queue for real-time position updates
      setLastWsMessage(message);
    },
    onNewMessageWithUrls: (url?: string) => {
      // Triggered by dev_task_complete when the final message contains URLs
      // Auto-open preview disabled as per user request
      console.log('🌐 [ChatPage] Build complete with URLs — auto-open/refresh preview disabled', url);
    },
    onProjectStatsUpdate: (updatedProjectId, tokenStats) => {
      // Update project cost display when token stats are updated
      console.log('📊 [ChatPage] Project stats updated for:', updatedProjectId, tokenStats);
      console.log('📊 [ChatPage] Current project ID:', projectId);
      console.log('📊 [ChatPage] Stats total cost:', tokenStats.total_cost);
      console.log('📊 [ChatPage] Current projectCost state:', projectCost);

      if (updatedProjectId === projectId && tokenStats.total_cost !== undefined) {
        console.log('📊 [ChatPage] ✅ Updating projectCost from', projectCost, 'to', tokenStats.total_cost);
        fetchProjectCost();
        console.log('📊 [ChatPage] ✅ projectCost state updated!');
      } else {
        console.warn('📊 [ChatPage] ❌ Not updating project cost:', {
          projectIdMatch: updatedProjectId === projectId,
          hasTotalCost: tokenStats.total_cost !== undefined,
          updatedProjectId,
          currentProjectId: projectId,
          totalCost: tokenStats.total_cost
        });
      }
    }
  });

  // ── Prompt Queue ──────────────────────────────────────────────────────────
  // Track the last incoming WebSocket message so usePromptQueue can react to
  // queue_position_update events for real-time position refreshes.
  const [lastWsMessage, setLastWsMessage] = useState<{ type: string; data?: any } | null>(null);

  // The job_id of the queued message currently being edited in the ChatInput.
  // null = no edit in progress.
  const [editingQueueJobId, setEditingQueueJobId] = useState<string | null>(null);
  // Snapshot of model/settings before entering edit mode — stored as refs so
  // they never appear in effect deps and don't trigger re-renders.
  const preEditModelRef = useRef<ModelPreference | null>(null);
  const preEditSettingsRef = useRef<Record<string, boolean> | null>(null);
  const preEditInteractionModeRef = useRef<InteractionMode | null>(null);

  // Always-current refs so the edit effect can snapshot them without a stale closure.
  const modelRef = useRef(model);
  modelRef.current = model;
  const agentSettingsRef = useRef(agentSettings);
  agentSettingsRef.current = agentSettings;
  const interactionModeRef = useRef(interactionMode);
  interactionModeRef.current = interactionMode;

  const promptQueue = usePromptQueue(projectId || '', lastWsMessage);
  queueRefreshRef.current = promptQueue.refresh;

  // Handles enter/exit edit mode driven by the local editing state (client-side only).
  // Fires only when editingJob or editingQueueJobId changes — clean two-item deps.
  useEffect(() => {
    if (promptQueue.editingJob && editingQueueJobId !== promptQueue.editingJob.job_id) {
      // Enter edit mode — snapshot via refs, no stale-closure risk.
      preEditModelRef.current = modelRef.current;
      preEditSettingsRef.current = agentSettingsRef.current;
      preEditInteractionModeRef.current = interactionModeRef.current;

      setMessage(promptQueue.editingJob.user_message || '');
      if (promptQueue.editingJob.model_preference) {
        setModelState(promptQueue.editingJob.model_preference as ModelPreference);
      }
      if (promptQueue.editingJob.settings) {
        setAgentSettingsState(promptQueue.editingJob.settings);
      }
      // Raw setter — don't clobber the user's localStorage mode preference.
      if (promptQueue.editingJob.interaction_mode) {
        setInteractionModeState(promptQueue.editingJob.interaction_mode as InteractionMode);
      }
      setEditingQueueJobId(promptQueue.editingJob.job_id);
    } else if (!promptQueue.editingJob && editingQueueJobId) {
      // Exit edit mode — job ran externally; restore user's original preferences.
      setEditingQueueJobId(null);
      setMessage('');
      setSelectedTicketTags([]);
      if (preEditModelRef.current !== null) {
        setModelState(preEditModelRef.current);
        preEditModelRef.current = null;
      }
      if (preEditSettingsRef.current !== null) {
        setAgentSettingsState(preEditSettingsRef.current);
        preEditSettingsRef.current = null;
      }
      if (preEditInteractionModeRef.current !== null) {
        setInteractionModeState(preEditInteractionModeRef.current);
        preEditInteractionModeRef.current = null;
      }
    }
  }, [promptQueue.editingJob, editingQueueJobId]);
  // ─────────────────────────────────────────────────────────────────────────

  // Initialize Brainstorm WebSocket hook for multi-window sync
  const brainstormWebSocket = useBrainstormWebSocket();


  // Determine active thread and messages based on mode

  const isBrainstormThread = mode === ChatMode.DESIGN;
  const messages = isBrainstormThread ? brainstormMessages : devAgent.messages;
  const isLoading = isBrainstormThread ? brainstormIsLoading : devAgent.isLoading;
  const isStreaming = isBrainstormThread ? brainstormIsStreaming : devAgent.isStreaming;
  const currentThreadId = isBrainstormThread ? brainstormThreadId : devAgent.currentThreadId;

  // When viewing original chat, display the segment's own paginated window
  const isViewingOriginalChat = !isBrainstormThread && !!devAgent.viewingSegmentId;
  const displayMessages = isViewingOriginalChat ? devAgent.viewingSegmentMessages : messages;
  // A historical window (loaded bottom is not the true tail) — on the active
  // branch or inside the segment view. Tail behaviors (auto-scroll, downward
  // pagination) key off these.
  const isWindowedView = !isBrainstormThread && !isViewingOriginalChat && devAgent.viewMode === 'window';
  const isSegmentWindowed = isViewingOriginalChat && devAgent.segmentViewMode === 'window';

  // Conversation navigator (Build Mode only): index of user turns for the
  // current view — active branch, or the viewed segment when scoped
  const navEnabled = mode === ChatMode.BUILD;
  const fetchNavIndex = useMemo(() => {
    const threadId = devAgent.currentThreadId;
    if (!navEnabled || !projectId || !threadId) return null;
    const segmentId = devAgent.viewingSegmentId ?? undefined;
    return async () =>
      (await versionControlApi.getChatIndex(projectId, threadId, segmentId)).items;
  }, [navEnabled, projectId, devAgent.currentThreadId, devAgent.viewingSegmentId]);
  const { items: navItems, refresh: refreshNavIndex } = useChatIndex(fetchNavIndex);

  // Re-sync the navigator index when a build-mode stream completes (the user
  // turn is persisted with its real DB id by then)
  const prevDevStreamingRef = useRef(false);
  useEffect(() => {
    if (prevDevStreamingRef.current && !devAgent.isStreaming) {
      refreshNavIndex();
    }
    prevDevStreamingRef.current = devAgent.isStreaming;
  }, [devAgent.isStreaming, refreshNavIndex]);

  // Count active panels
  const activePanelsCount = [
    showDocumentsPanel,
    showCodebasePanel,
    showDesignSystemPanel,
    showPreviewPanel,
    showDatabasePanel,
    selectedArtifact !== null,
    selectedKnowledgeDoc !== null
  ].filter(Boolean).length;

  // Determine if we should use full width for messages (when more than 2 panels are active)
  const isFullWidthMessages = activePanelsCount >= 1;

  // Ref to prevent multiple simultaneous message loads
  const isLoadingMessagesRef = useRef(false);
  // Ref to track which thread is currently being loaded (to ignore stale responses)
  const loadingThreadIdRef = useRef<string | null>(null);
  
  // 🔄 CHECK STREAMING STATUS (for persistent streaming)
  // Use ref to track if we're already polling to prevent multiple polling loops
  const isPollingRef = useRef(false);
  const pollingTimeoutRef = useRef<number | null>(null);
  
  // Use ref to store loadBrainstormThreadMessages to avoid circular dependency
  const loadBrainstormThreadMessagesRef = useRef<((threadId: string, limit?: number, beforeMessageId?: string, skipStreamingCheck?: boolean) => Promise<void>) | null>(null);
  
  // Define checkStreamingStatus first (used as fallback when WebSocket is not available)
  const checkStreamingStatus = useCallback(async (threadId: string) => {
    if (!threadId || !projectId) return;

    try {
      const response = await api.get(`/chat/projects/${projectId}/threads/${threadId}/streaming-status`);
      const status = response.data;

      if (status.is_streaming && status.status === 'active') {
        // Still streaming - show "Designing..." message
        setBrainstormMessages(prev => {
          // Find the last assistant message
          let lastAssistantIndex = -1;
          for (let i = prev.length - 1; i >= 0; i--) {
            if (prev[i].role === 'assistant') {
              lastAssistantIndex = i;
              break;
            }
          }
          
          if (lastAssistantIndex !== -1) {
            const updatedMessages = [...prev];
            updatedMessages[lastAssistantIndex] = {
              ...updatedMessages[lastAssistantIndex],
              isThinking: true,
              thinkingMessage: 'Veylo Agent is designing...',
              isStreaming: true
            };
            return updatedMessages;
          } else {
            // Fallback: Create new message if no assistant message exists
            const streamingMessage: Message = {
              id: `streaming-${status.assistant_message_id}`,
              role: 'assistant',
              content: '',
              timestamp: new Date(),
              isThinking: true,
              thinkingMessage: 'Veylo Agent is designing...',
              isStreaming: true
            };
            return [...prev, streamingMessage];
          }
        });
        
        setBrainstormIsStreaming(true);
        setBrainstormIsLoading(false);
        
        // Poll again after 5 seconds (optimized for performance)
        if (isPollingRef.current) {
          pollingTimeoutRef.current = window.setTimeout(() => checkStreamingStatus(threadId), 5000);
        }
        
      } else if (status.status === 'completed' || status.status === 'cancelled') {
        // Streaming completed or cancelled - reload messages to get the final response
        setBrainstormIsStreaming(false);
        setBrainstormIsLoading(false);
        isPollingRef.current = false; // Stop polling
        // Use ref to call loadBrainstormThreadMessages
        if (loadBrainstormThreadMessagesRef.current) {
          await loadBrainstormThreadMessagesRef.current(threadId, 10, undefined, true);
        }
        
      } else if (status.status === 'error') {
        // Error occurred
        console.error('❌ Background streaming error:', status.error_message);
        setBrainstormIsStreaming(false);
        setBrainstormIsLoading(false);
        isPollingRef.current = false; // Stop polling
        
        // Show error message
        setBrainstormMessages(prev => {
          const withoutThinking = prev.filter(msg => !msg.isThinking);
          const errorMsg: Message = {
            id: generateMessageId('error'),
            role: 'assistant',
            content: status.error_message || 'An error occurred during generation.',
            timestamp: new Date(),
            isThinking: false
          };
          return [...withoutThinking, errorMsg];
        });
        
      } else {
        // No active streaming detected via API
        console.log('📡 [Brainstorm] checkStreamingStatus: No active streaming detected, status=', status.status);
        setBrainstormIsStreaming(false);
        setBrainstormIsLoading(false);
        isPollingRef.current = false; // Stop polling
      }
      
    } catch (err) {
      console.error('❌ Error checking streaming status:', err);
      setBrainstormIsStreaming(false);
      setBrainstormIsLoading(false);
      isPollingRef.current = false; // Stop polling on error
    }
  }, [projectId]);
  
  // Load brainstorm thread messages
  const loadBrainstormThreadMessages = useCallback(async (threadId: string, limit = 10, beforeMessageId?: string, skipStreamingCheck = false) => {
    if (!threadId) {
      setBrainstormMessages([]);
      setBrainstormHasMoreMessages(false);
      return;
    }

    // Prevent multiple simultaneous loads for the SAME thread
    if (isLoadingMessagesRef.current && loadingThreadIdRef.current === threadId) {
      return;
    }

    isLoadingMessagesRef.current = true;
    loadingThreadIdRef.current = threadId;

    // Show skeleton for initial load (not for pagination)
    if (!beforeMessageId) {
      setIsInitialLoad(true);
    }

    try {
      // Build query parameters for pagination
      const params = new URLSearchParams();
      if (limit) params.append('limit', limit.toString());
      if (beforeMessageId) params.append('before_message_id', beforeMessageId);

      const response = await api.get(`/chat/projects/${projectId}/threads/${threadId}/messages/?${params.toString()}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });

      // CRITICAL: Check if this response is for the thread we're still interested in
      if (loadingThreadIdRef.current !== threadId) {
        console.warn('⚠️ Ignoring stale response for thread:', threadId);
        return;
      }

      const threadMessages = (response.data?.messages || []).map((msg: any) => ({
        id: msg._id || msg.id,
        role: msg.role,
        content: msg.content,
        timestamp: typeof msg.timestamp === 'number' ? new Date(msg.timestamp * 1000) : new Date(msg.timestamp),
        images: msg.images || msg.image_urls || [],
        imageIds: msg.image_ids || [],
        agentLogs: msg.agent_logs || [],
        toolExecutions: msg.tool_executions || [],
        isThinking: msg.is_thinking || false,
        thinkingMessage: msg.thinking_message || '',
        tokenUsage: msg.tokenUsage,
        hasTokenUsage: msg.hasTokenUsage || false,
        brainstormTiming: msg.brainstormTiming,
        model_preference: msg.model_preference,
        user_info: msg.user_info || null,
        documents: msg.documents || [],  // Unified documents array from API
        attachments: msg.attachments || []  // Unified attachments array with source information
      }));

      // Update hasMore state based on pagination metadata
      setBrainstormHasMoreMessages(response.data?.pagination?.has_more || false);

      // Handle pagination: if beforeMessageId is provided, prepend to existing messages
      if (beforeMessageId) {
        // This is a pagination request - prepend older messages
        // Use functional update to get the latest messages state
        setBrainstormMessages(prevMessages => {
          const hasMore = response.data?.pagination?.has_more || false;
          if (!hasMore) {
            const hasWelcome = prevMessages.some(msg => msg.id === WELCOME_MESSAGE_ID);
            if (!hasWelcome) {
              const welcomeMessage = {
                id: WELCOME_MESSAGE_ID,
                role: 'assistant',
                content: WELCOME_MESSAGE_WEB,
                created_at: Date.now() / 1000,
              };
              return [welcomeMessage, ...threadMessages, ...prevMessages];
            }
          }
          return [...threadMessages, ...prevMessages];
        });
      } else {
        // This is an initial load or refresh - replace all messages
        // BUT preserve any thinking message that WebSocket might have added while we were loading
        const lastMsg = threadMessages[threadMessages.length - 1];
        
        // Simple logic: If last assistant message has no content, it's still streaming
        // Show "Designing..." state. WebSocket will notify when complete.
        if (lastMsg?.role === 'assistant' && !lastMsg.content) {
          const messagesWithThinking = threadMessages.map((msg: Message, index: number) => {
            if (index === threadMessages.length - 1 && msg.role === 'assistant' && !msg.content) {
              return {
                ...msg,
                isThinking: true,
                thinkingMessage: 'Veylo Agent is designing...',
                isStreaming: true
              };
            }
            return msg;
          });
          setBrainstormMessages(messagesWithThinking);
          setBrainstormIsStreaming(true);
        } else {
          const hasMore = response.data?.pagination?.has_more || false;
          const shouldAddWelcomeMessage = threadMessages.length === 0 || !hasMore;
          if (shouldAddWelcomeMessage) {
            const welcomeMessage: Message = {
              id: WELCOME_MESSAGE_ID,
              role: 'assistant',
              content: WELCOME_MESSAGE_WEB,
              timestamp: new Date(),
            };
            setBrainstormMessages([welcomeMessage, ...threadMessages]);
          } else {
            setBrainstormMessages(threadMessages);
          }
        }
        
        // Check streaming status as fallback (WebSocket is primary)
        // Skip if this is a reload after streaming completion
        // Also skip if WebSocket already detected active streaming to avoid race condition
        // Use ref to get the current value (avoids closure issues)
        if (threadId && projectId && !skipStreamingCheck && !brainstormIsStreamingRef.current) {
          console.log('📡 [Brainstorm] Starting streaming status check (fallback)');
          isPollingRef.current = true;
          checkStreamingStatus(threadId);
        } else if (brainstormIsStreamingRef.current) {
          console.log('📡 [Brainstorm] Skipping streaming status check - WebSocket already detected streaming');
        }
      }

      // Hide skeleton after messages are loaded (for initial load)
      if (!beforeMessageId) {
        setIsInitialLoad(false);
      }
    } catch (err) {
      console.error('Error loading brainstorm thread messages:', err);
      if (!beforeMessageId) {
        const errorMessage: Message = {
          id: generateMessageId('error-load'),
          role: 'assistant',
          content: 'Failed to load conversation history. Please refresh the page or try again.',
          timestamp: new Date(),
          isThinking: false
        };
        setBrainstormMessages([errorMessage]);
        setBrainstormHasMoreMessages(false);
        // Hide skeleton on error
        setIsInitialLoad(false);
      }
    } finally {
      // Always reset loading flag (only if we're still loading this thread)
      if (loadingThreadIdRef.current === threadId) {
        isLoadingMessagesRef.current = false;
        loadingThreadIdRef.current = null;
      }
    }
  }, [projectId, checkStreamingStatus, currentProject]);
  
  // Store loadBrainstormThreadMessages in ref for checkStreamingStatus to use
  useEffect(() => {
    loadBrainstormThreadMessagesRef.current = loadBrainstormThreadMessages;
  }, [loadBrainstormThreadMessages]);

  // 🔄 CLEANUP POLLING ON UNMOUNT OR THREAD CHANGE
  useEffect(() => {
    // Cleanup: stop polling when thread changes or component unmounts
    return () => {
      isPollingRef.current = false;
      if (pollingTimeoutRef.current) {
        clearTimeout(pollingTimeoutRef.current);
        pollingTimeoutRef.current = null;
      }
    };
  }, [mode, brainstormThreadId, projectId]);

  // BRAINSTORM WEBSOCKET MESSAGE HANDLER (for multi-window sync)
  const handleBrainstormWebSocketMessage = useCallback((message: BrainstormWebSocketMessage) => {
    console.log('📡 [Brainstorm WS] Received message:', message.type, message.data);
    switch (message.type) {
      case 'brainstorm_user_message_added': {
        // Another window added a message - add to local state if not already present
        console.log('📡 [Brainstorm WS] User message added from another window');
        const messageId = message.data.message_id;
        const incomingContent = message.data.content;
        
        setBrainstormMessages(prev => {
          // Find the last user message and check if it has the same content
          const lastUserMessage = [...prev].reverse().find(m => m.role === 'user');
          
          // If last user message has same content, it's already added (optimistic update)
          // Just skip this WebSocket message to avoid flicker
          if (lastUserMessage && lastUserMessage.content === incomingContent) {
            return prev;
          }
          
          // Add new message from WebSocket (different window)
          const newMessage: Message = {
            id: generateMessageId('user-ws', messageId),
            role: 'user',
            content: incomingContent || '',
            timestamp: message.data.timestamp ? new Date(message.data.timestamp) : new Date(),
            isThinking: false,
            user_info: message.data.user_info
          };
          return [...prev, newMessage];
        });
        break;
      }

      case 'brainstorm_streaming_started': {
        // Streaming started (possibly from another window)
        console.log('📡 [Brainstorm WS] Received streaming_started:', message.data);
        // Check if we're already showing "Designing..." indicator
        if (brainstormIsStreaming) {
          console.log('📡 [Brainstorm WS] Already streaming, skipping');
          return;
        }
        console.log('📡 [Brainstorm WS] Setting isStreaming=true');
        setBrainstormIsStreaming(true);
        setBrainstormIsLoading(false);

        // Add "Designing..." indicator if not already present
        setBrainstormMessages(prev => {
          const hasThinking = prev.some(m => m.isThinking);
          if (hasThinking) {
            return prev;
          }
          const thinkingMessage: Message = {
            id: generateMessageId('streaming', message.data.assistant_message_id),
            role: 'assistant',
            content: '',
            timestamp: new Date(),
            isThinking: true,
            thinkingMessage: 'Veylo Agent is designing...',
            isStreaming: true
          };
          return [...prev, thinkingMessage];
        });
        break;
      }

      case 'brainstorm_streaming_completed': {
        // Streaming completed - reload messages to get final content
        console.log('📡 [Brainstorm WS] Received streaming_completed:', message.data);
        console.log('📡 [Brainstorm WS] Setting isStreaming=false, re-enabling input');
        setBrainstormIsStreaming(false);
        setBrainstormIsLoading(false);
        isPollingRef.current = false;

        // Reload messages to get the complete response
        const threadId = message.data.thread_id || brainstormThreadId;
        if (threadId) {
          loadBrainstormThreadMessages(threadId, 10, undefined, true);
        }

        // Refresh project cost (fetch directly to avoid dependency)
        // Use project_id from message if available, fallback to component's projectId
        const brainstormProjectId = message.data?.project_id || projectId;
        
        if (brainstormProjectId) {
          api.get(`/projects/${brainstormProjectId}/stats`).then(response => {
            const totalCost = parseFloat(response.data.total_cost) || 0;
            const totalDuration = parseInt(response.data.total_duration_ms) || 0;
            setProjectCost(totalCost);
            setProjectDuration(totalDuration);
          }).catch(err => {
            console.error('Failed to fetch project cost:', err);
          });
        }
        break;
      }

      case 'brainstorm_streaming_cancelled': {
        // Streaming was cancelled
        setBrainstormIsStreaming(false);
        setBrainstormIsLoading(false);
        isPollingRef.current = false; // Stop polling

        // Reload messages to get the cancelled message from DB
        const threadId = message.data.thread_id || brainstormThreadId;
        if (threadId) {
          loadBrainstormThreadMessages(threadId, 10, undefined, true);
        }
        break;
      }

      case 'brainstorm_streaming_status': {
        // Initial streaming status on connect (received when passive window connects)
        console.log('📡 [Brainstorm WS] Received streaming_status:', message.data);
        if (message.data.is_streaming && message.data.status === 'active') {
          console.log('📡 [Brainstorm WS] Active streaming detected - disabling input');
          setBrainstormIsStreaming(true);

          // Show thinking indicator if not already present AND if messages are loaded
          setBrainstormMessages(prev => {
            const hasThinking = prev.some(m => m.isThinking);
            if (hasThinking) return prev;

            // Don't add thinking message if messages haven't loaded yet
            // Let the message loading flow handle it to avoid flash of "Designing..." before messages
            if (prev.length === 0) return prev;

            const thinkingMessage: Message = {
              id: generateMessageId('streaming', message.data.assistant_message_id),
              role: 'assistant',
              content: '',
              timestamp: new Date(),
              isThinking: true,
              thinkingMessage: 'Veylo Agent is designing...',
              isStreaming: true
            };
            return [...prev, thinkingMessage];
          });
        }
        break;
      }

      default:
        // Unhandled message type
        break;
    }
  }, [brainstormThreadId, brainstormIsStreaming, loadBrainstormThreadMessages, projectId]);

  // 📡 BRAINSTORM WEBSOCKET CONNECTION (for multi-window sync)
  // Destructure stable callbacks to avoid the whole hook object in deps (prevents render loops)
  const { connectToThread: bsConnectToThread, disconnect: bsDisconnect } = brainstormWebSocket;

  useEffect(() => {
    // Only connect when in Design mode with a valid thread
    if (mode !== ChatMode.DESIGN || !brainstormThreadId || !projectId) {
      // Disconnect if switching away from Design mode
      if (brainstormWebSocket.connectedThreadId) {
        bsDisconnect();
      }
      return;
    }

    bsConnectToThread(brainstormThreadId, {
      onMessage: handleBrainstormWebSocketMessage,
      onConnect: () => {
        // Stop polling when WebSocket connects
        isPollingRef.current = false;
        if (pollingTimeoutRef.current) {
          clearTimeout(pollingTimeoutRef.current);
          pollingTimeoutRef.current = null;
        }
      },
      onReconnect: () => {
        // Stop polling and reload messages to catch up
        isPollingRef.current = false;
        if (pollingTimeoutRef.current) {
          clearTimeout(pollingTimeoutRef.current);
          pollingTimeoutRef.current = null;
        }
        loadBrainstormThreadMessages(brainstormThreadId, 10, undefined, true);
      },
      onDisconnect: () => {
        // Start polling fallback if still streaming
        if (brainstormIsStreaming && brainstormThreadId) {
          isPollingRef.current = true;
          checkStreamingStatus(brainstormThreadId);
        }
      },
      onError: (error) => {
        console.error('📡 [Brainstorm WS] Error:', error);
        // Start polling fallback if still streaming
        if (brainstormIsStreaming && brainstormThreadId) {
          isPollingRef.current = true;
          checkStreamingStatus(brainstormThreadId);
        }
      }
    }).catch(error => {
      console.error('📡 [Brainstorm WS] Connection failed:', error);
      // Fall back to polling if WebSocket fails
      if (brainstormIsStreaming && brainstormThreadId) {
        isPollingRef.current = true;
        checkStreamingStatus(brainstormThreadId);
      }
    });

    // Cleanup on unmount or when dependencies change
    return () => {
      // Don't disconnect here - let the hook manage its own lifecycle
    };
  }, [mode, brainstormThreadId, projectId, bsConnectToThread, bsDisconnect, handleBrainstormWebSocketMessage, loadBrainstormThreadMessages, brainstormIsStreaming, checkStreamingStatus]);

  // Load more messages handler - works for both modes
  const loadMore = useCallback(async (container: HTMLDivElement) => {
    if (isLoadingRef.current) return;

    // Check if we should load more based on mode
    if (isBrainstormThread) {
      // For brainstorm mode
      if (!brainstormHasMoreMessages || !brainstormThreadId) return;
    } else if (isViewingOriginalChat) {
      // Segment view (own paginated window)
      if (!devAgent.segmentHasMoreOlder) return;
    } else {
      // For development mode
      if (!devAgent.hasMoreMessages || !devAgent.currentThreadId) return;
    }

    isLoadingRef.current = true;

    if (isBrainstormThread) {
      setBrainstormIsLoadingMore(true);
    } else {
      setDevIsLoadingMore(true);
    }

    const prevScrollHeight = container.scrollHeight;
    const prevScrollTop = container.scrollTop;

    try {
      if (isBrainstormThread && brainstormThreadId) {
        // Load more brainstorm messages - use oldest message ID for cursor-based pagination
        // Use functional state update to get the latest messages
        setBrainstormMessages(prevMessages => {
          if (prevMessages.length > 0) {
            const oldestMessage = prevMessages[0];
            loadBrainstormThreadMessages(brainstormThreadId, 10, oldestMessage.id);
          }
          return prevMessages; // Return unchanged for now, will be updated by loadBrainstormThreadMessages
        });
      } else if (isViewingOriginalChat) {
        await devAgent.loadSegmentOlder();
      } else if (devAgent.currentThreadId) {
        // Load more development messages
        await devAgent.loadMoreMessages(devAgent.currentThreadId);
      }
    } finally {
      // Preserve scroll position after new messages are prepended
      requestAnimationFrame(() => {
        const newScrollHeight = container.scrollHeight;
        container.scrollTop = newScrollHeight - prevScrollHeight + prevScrollTop;
        isLoadingRef.current = false;
        setBrainstormIsLoadingMore(false);
        setDevIsLoadingMore(false);
      });
    }
  }, [devAgent, isBrainstormThread, isViewingOriginalChat, brainstormHasMoreMessages, brainstormThreadId, loadBrainstormThreadMessages]);

  // Load the next newer page when scrolling toward the bottom of a historical
  // window. Appends below the viewport, so no scroll compensation is needed;
  // scrolling down through the appended page re-arms the trigger for the next.
  const loadNewer = useCallback(async () => {
    if (isLoadingRef.current || !devAgent.currentThreadId) return;
    isLoadingRef.current = true;
    setDevIsLoadingNewer(true);
    try {
      if (isViewingOriginalChat) await devAgent.loadSegmentNewer();
      else await devAgent.loadNewerMessages(devAgent.currentThreadId);
    } finally {
      isLoadingRef.current = false;
      setDevIsLoadingNewer(false);
    }
  }, [devAgent, isViewingOriginalChat]);

  // Shared eligibility checks for the two pagination directions, used by the
  // scroll-zone triggers and by the wheel fallback at the scroll boundaries
  const maybeLoadOlder = useCallback((container: HTMLDivElement) => {
    const hasOlder = isBrainstormThread
      ? brainstormHasMoreMessages
      : isViewingOriginalChat
        ? devAgent.segmentHasMoreOlder
        : devAgent.hasMoreMessages;
    const isAlreadyLoading = isBrainstormThread ? brainstormIsLoadingMore : devIsLoadingMore;
    if (hasOlder && !isAlreadyLoading && !isLoadingRef.current) {
      loadMore(container);
    }
  }, [isBrainstormThread, isViewingOriginalChat, brainstormHasMoreMessages, devAgent.segmentHasMoreOlder, devAgent.hasMoreMessages, brainstormIsLoadingMore, devIsLoadingMore, loadMore]);

  const maybeLoadNewer = useCallback(() => {
    if ((isWindowedView || isSegmentWindowed) && !devIsLoadingNewer && !isLoadingRef.current) {
      loadNewer();
    }
  }, [isWindowedView, isSegmentWindowed, devIsLoadingNewer, loadNewer]);

  // Conversation navigator wiring: the headless useChatWindow hook owns jump
  // orchestration and the user-intent-gated pagination triggers; this adapter
  // binds it to the active branch or the segment window, whichever is shown
  const navAdapter = useMemo(() => ({
    jumpToMessage: async (id: string) => {
      // A jump must not race another load
      if (!devAgent.currentThreadId || isLoadingRef.current) return null;
      isLoadingRef.current = true;
      setDevIsLoadingMore(true);
      try {
        return isViewingOriginalChat
          ? await devAgent.loadSegmentFrom(id)
          : await devAgent.loadMessagesFrom(devAgent.currentThreadId, id);
      } finally {
        isLoadingRef.current = false;
        setDevIsLoadingMore(false);
      }
    },
    loadOlder: maybeLoadOlder,
    loadNewer: maybeLoadNewer,
  }), [isViewingOriginalChat, devAgent, maybeLoadOlder, maybeLoadNewer]);

  const chatWindow = useChatWindow({
    containerRef: chatContainerRef,
    messages: displayMessages,
    getMessageElement,
    adapter: navAdapter,
    // A jump target that can't be loaded means the navigator index is stale
    // (message deleted / hidden by a revert)
    onStaleTarget: refreshNavIndex,
  });

  // Handle scroll event for smart auto-scroll detection; the pagination
  // triggers live in the chat-window hook
  const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    const container = e.currentTarget;
    const { scrollTop, scrollHeight, clientHeight } = container;

    // Check if user is at bottom
    const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
    const threshold = 50; // pixels from bottom

    const atBottom = distanceFromBottom <= threshold;
    setIsAtBottom(atBottom);

    // If user scrolled up manually (away from bottom), set the flag
    if (!atBottom && !hasUserScrolledUp) {
      setHasUserScrolledUp(true);
    }

    // If user scrolled back to bottom manually, re-enable auto-scroll
    if (atBottom && hasUserScrolledUp) {
      setHasUserScrolledUp(false);
    }

    chatWindow.onScroll(e);
  }, [hasUserScrolledUp, chatWindow]);

  const createNewThread = async (skipSkeletonForNewThread = false): Promise<string> => {
    if (!user || !projectId) throw new Error('Missing user or project');

    const promise = (async () => {
      try {
        const title = generateThreadTitle();

        if (mode === ChatMode.BUILD) {
          const response = await api.post(`/dev-agent/projects/${projectId}/threads/`, { title });
          const threadData = response.data;
          const threadId = threadData.id;
          // Only show skeleton if we're not creating a new thread for sending the first message
          if (!skipSkeletonForNewThread) {
            setIsInitialLoad(true);
          }
          await devAgent.selectThread(threadId);
          return threadId;
        } else {
          // Design mode - create brainstorm thread
          const response = await api.post('/chat/threads/', {
            project_id: projectId,
            user_id: user.id,
            title: title
          });
          const threadData = response.data;
          const threadId = threadData.id;
          setBrainstormThreadId(threadId);
          return threadId;
        }
      } finally {

      }
    })();
    return promise;
  };

  // Handle thread selection from RequirementGroups
  const handleThreadSelect = async (threadId: string) => {
    // Skip if already on this thread (prevents double load on URL changes)
    if (threadId && brainstormThreadId === threadId) {
      return;
    }
    
    await performThreadSelect(threadId);
    // Save position to localStorage when thread is selected in design mode
    if (projectId && mode === ChatMode.DESIGN) {
      saveProjectPosition(projectId, 'design', threadId || null);
      // Update URL to include threadId only if it's different from current URL
      if (threadId && urlThreadId !== threadId) {
        navigate(`/chat/${projectId}/design/${threadId}`, { replace: true });
      }
    }
  };

  const performThreadSelect = async (threadId: string) => {
    // Handle empty thread selection (clear selection)
    if (!threadId || threadId === '') {
      await devAgent.selectThread(undefined);
      setBrainstormThreadId(undefined);
      setBrainstormMessages([]);
      setBrainstormHasMoreMessages(false);
      return;
    }

    // Check if this is a brainstorm thread (requirement folder thread)
    try {
      const threadResponse = await api.get(`/chat/threads/${projectId}`);
      const allThreads = threadResponse.data || [];
      const thread = allThreads.find((t: any) => t.id === threadId);

      if (thread && thread.mode === 'brainstorm') {
        // Clear any development thread first
        await devAgent.selectThread(undefined);
        
        // Load brainstorm thread
        setBrainstormThreadId(threadId);
        setBrainstormMessages([]);
        await loadBrainstormThreadMessages(threadId);
      } else {
        // This is a development thread
        // Clear any brainstorm thread first
        setBrainstormThreadId(undefined);
        setBrainstormMessages([]);
        setBrainstormHasMoreMessages(false);
        setIsInitialLoad(true);
        await devAgent.selectThread(threadId);
      }
    } catch (error) {
      console.error('Error detecting thread mode:', error);
      // Fallback to development mode
      await devAgent.selectThread(threadId);
    }
  };

  // Panel close handlers with animation
  const handleCloseDocumentsPanel = () => {
    setIsDocumentsPanelClosing(true);
    setTimeout(() => {
      setShowDocumentsPanel(false);
      setIsDocumentsPanelClosing(false);
    }, 250); // Match animation duration
  };

  const handleCloseCodebasePanel = () => {
    setIsCodebasePanelClosing(true);
    setTimeout(() => {
      setShowCodebasePanel(false);
      setIsCodebasePanelClosing(false);
      setInitialCodebaseFile(null); // Clear the initial file when closing
    }, 250); // Match animation duration
  };

  const handleFileClick = (filePath: string) => {
    console.log('📄 [ChatPage] File clicked:', filePath);
    
    // Only allow file opening in BUILD mode
    if (mode !== ChatMode.BUILD) {
      console.warn('⚠️ [ChatPage] File click ignored - not in BUILD mode');
      return;
    }
    
    // Close other panels if open
    if (selectedArtifact) {
      setSelectedArtifact(null);
    }
    if (showDocumentsPanel) {
      handleCloseDocumentsPanel();
    }
    if (showPreviewPanel) {
      handleClosePreviewPanel();
    }
    if (showDesignSystemPanel) {
      handleCloseDesignSystemPanel();
    }
    if (showDatabasePanel) {
      handleCloseDatabasePanel();
    }
    
    console.log('✅ [ChatPage] Opening codebase panel with file:', filePath);
    setInitialCodebaseFile(filePath);
    setShowCodebasePanel(true);
  };

  const handleClosePreviewPanel = useCallback(() => {
    setIsPreviewPanelClosing(true);
    setTimeout(() => {
      setShowPreviewPanel(false);
      setIsPreviewPanelClosing(false);
    }, 250);
  }, []);

  const handleCloseDatabasePanel = () => {
    setIsDatabasePanelClosing(true);
    setTimeout(() => {
      setShowDatabasePanel(false);
      setIsDatabasePanelClosing(false);
    }, 250);
  };

  const handleCloseArtifactSharePannel = () => {
    setIsArtifactSharePannelClosing(true);
    setTimeout(() => {
      setShowArtifactSharePannel(false);
      setIsArtifactSharePannelClosing(false);
    }, 250); // Match animation duration
  };

  const handleCloseKnowledgeDocSharePannel = () => {
    setIsKnowledgeDocSharePannelClosing(true);
    setTimeout(() => {
      setShowKnowledgeDocSharePannel(false);
      setIsKnowledgeDocSharePannelClosing(false);
    }, 250); // Match animation duration
  };

  const handleCloseTestRecordingSharePannel = () => {
    setIsTestRecordingSharePannelClosing(true);
    setTimeout(() => {
      setShowTestRecordingSharePannel(false);
      setIsTestRecordingSharePannelClosing(false);
      // Unmount so the next open re-initializes with a freshly fetched share state
      setTestRecordingToShare(null);
    }, 250); // Match animation duration
  };

  const handleInlineCheckpointRevert = async (messageId: string) => {
    if (!projectId || !devAgent.currentThreadId) return;
    try {
      setRevertingMessageId(messageId);
      devAgent.clearViewingSegment();
      await versionControlApi.revertToCheckpoint(projectId, devAgent.currentThreadId, messageId);

      // Code restored — update chat history
      setRevertingMessageId(null);
      devAgent.setRevertStatus('rebuilding');
      await devAgent.loadMessages(devAgent.currentThreadId, true);
      // Segment visibility changed — rebuild the navigator index
      refreshNavIndex();

      // Poll app-lifecycle status until containers are healthy or timeout.
      // The backend caches `compose ps` for ~15s, so polling faster yields
      // the same cached value. We poll every 15s for up to ~5 min.
      const pollAppStatus = async () => {
        const maxAttempts = 20;
        const intervalMs = 15_000;
        for (let i = 0; i < maxAttempts; i++) {
          await new Promise(r => setTimeout(r, intervalMs));
          try {
            const res = await api.get(`/dev-agent/projects/${projectId}/app-lifecycle/status`);
            const services = res.data?.services || [];
            const allHealthy = services.length > 0 && services.every(
              (s: any) => s.status === 'running' || s.state === 'running'
            );
            if (allHealthy) {
              devAgent.setRevertStatus(null);
              return;
            }
          } catch {
            // Status endpoint might fail during rebuild, keep polling
          }
        }
        devAgent.setRevertStatus(null);
      };
      pollAppStatus();
    } catch (err) {
      console.error('Failed to revert to checkpoint:', err);
      setRevertingMessageId(null);
      devAgent.setRevertStatus(null);
    }
  };

  const handlePanelToggle = (panelToToggle: 'documents' | 'preview' | 'codebase' | 'database') => {
    const isPanelOpen =
      (panelToToggle === 'documents' && showDocumentsPanel) ||
      (panelToToggle === 'preview' && showPreviewPanel) ||
      (panelToToggle === 'codebase' && showCodebasePanel) ||
      (panelToToggle === 'database' && showDatabasePanel);

    if (isPanelOpen) {
      if (panelToToggle === 'documents') handleCloseDocumentsPanel();
      if (panelToToggle === 'preview') handleClosePreviewPanel();
      if (panelToToggle === 'codebase') handleCloseCodebasePanel();
      if (panelToToggle === 'database') handleCloseDatabasePanel();
      return;
    }

    if (selectedArtifact) {
      setSelectedArtifact(null);
    }
    if (selectedKnowledgeDoc) {
      setSelectedKnowledgeDoc(null);
    }

    // Check if any other panel is open and close it
    let otherPanelOpen = false;
    if (showDocumentsPanel) {
      handleCloseDocumentsPanel();
      otherPanelOpen = true;
    } else if (showCodebasePanel) {
      handleCloseCodebasePanel();
      otherPanelOpen = true;
    } else if (showPreviewPanel) {
      handleClosePreviewPanel();
      otherPanelOpen = true;
    } else if (showDatabasePanel) {
      handleCloseDatabasePanel();
      otherPanelOpen = true;
    }

    const openTargetPanel = () => {
      if (panelToToggle === 'documents') setShowDocumentsPanel(true);
      if (panelToToggle === 'preview') setShowPreviewPanel(true);
      if (panelToToggle === 'codebase') {
        setInitialCodebaseFile(null);
        setShowCodebasePanel(true);
      }
      if (panelToToggle === 'database') setShowDatabasePanel(true);
    };

    if (otherPanelOpen) {
      setTimeout(openTargetPanel, 250);
    } else {
      openTargetPanel();
    }
  };


  const handleSendMessage = async (msg: string, attachments?: Attachment[], model?: ModelPreference, settings?: Record<string, boolean>) => {
    await performSendMessage(msg, attachments, model, settings);
  };

  const performSendMessage = async (msg: string, attachments?: Attachment[], model?: ModelPreference, settings?: Record<string, boolean>) => {
    // Check if there's any content to send (message or attachments)
    const hasContent = msg.trim() || (attachments && attachments.length > 0);

    if (!hasContent || !projectId) {
      return;
    }

    // Clear the input immediately before processing
    setMessage('');

    // Always scroll to bottom when user sends a new message
    setHasUserScrolledUp(false);

    // Set sending state immediately to disable input while processing
    if (mode === ChatMode.BUILD) {
      setIsSendingMessage(true);
    } else if (mode === ChatMode.DESIGN) {
      // For brainstorm mode, set streaming state immediately to disable input
      console.log('📡 [Brainstorm] Setting isStreaming=true immediately on send');
      setBrainstormIsStreaming(true);
    }

    try {
      // Get or create thread
      let threadId = currentThreadId;

      // If no thread, create a new one
      if (!threadId) {
        console.log('📊 [ChatPage] No thread available, creating new thread');
        // Skip skeleton when creating a new thread for sending the first message
        threadId = await createNewThread(true);
      }

      // If viewing a historical window, return to the latest page first so
      // the optimistic message lands adjacent to the thread's true tail
      if (mode === ChatMode.BUILD && devAgent.viewMode === 'window') {
        await devAgent.loadMessages(threadId, true, 20);
      }

      // Route to appropriate handler based on mode
      if (mode === ChatMode.DESIGN) {
        await handleBrainstormMessage(threadId, msg, attachments, model);
      } else {
        // Build mode: serialize selected ticket @-references into the message
        // ({{ticket:TKT-001|label}}) so the backend can resolve them.
        const outgoing = (mode === ChatMode.BUILD && selectedTicketTags.length)
          ? serializeTicketTagsIntoContent(msg, selectedTicketTags)
          : msg;
        if (selectedTicketTags.length) setSelectedTicketTags([]);
        await handleEnhancedWorkflowMessage(threadId, outgoing, attachments, model, settings);
      }
    } finally {
      // Reset sending state after thread is ready and message handler started
      setIsSendingMessage(false);
      // Pick up the newly persisted user turn in the navigator rail
      if (mode === ChatMode.BUILD) {
        refreshNavIndex();
      }
    }
  };


  // Enhanced workflow handler - ALL development messages go through backend classification
  const handleEnhancedWorkflowMessage = async (threadId: string, message: string, attachments?: Attachment[], model?: ModelPreference, settings?: Record<string, boolean>) => {
    // Extract attachment IDs for the user message display
    const imageIds = attachments?.filter(attachment => attachment.source === 'image').map(attachment => attachment.id);
    const fileIds = attachments?.filter(attachment => attachment.source === 'file' || attachment.source === 'cab_markdown').map(attachment => attachment.id);
    const artifactIds = attachments?.filter(attachment => attachment.source === 'artifact').map(attachment => attachment.id);
    const fileNames = attachments?.map(attachment => attachment.name);

    // Add user message to UI immediately (optimistic update) - same pattern as Design mode
    const userMessage: Message = {
      id: generateMessageId('user-optimistic'),
      role: 'user',
      // Keep the serialized {{ticket:...}} tokens so the renderer highlights
      // each referenced ticket as a chip (matches the stored/socket content).
      content: message,
      timestamp: new Date(),
      fileIds: fileIds || [],
      artifactIds: artifactIds || [],
      imageIds: imageIds || [],
      fileNames: fileNames || [],
      attachments: attachments || []
    };

    // Add user message first, then thinking message
    devAgent.setMessages((prev: Message[]) => [...prev, userMessage]);

    try {
      // Show initial processing state and enable stop button
      devAgent.addProcessingMessage('Veylo Agent Thinking...');
      devAgent.setIsStreaming(true);

      const startPath = interactionMode === InteractionMode.ASK ? 'ask' : 'chat';
      // Ask mode is read-only — Agent settings (e.g. functional test) don't apply,
      // so never attach them to an Ask message.
      const effectiveSettings = interactionMode === InteractionMode.ASK ? {} : (settings ?? agentSettings);
      const response = await api.post(`/dev-agent/projects/${projectId}/${startPath}/start`, {
        content: message,
        image_ids: imageIds || [],
        file_ids: fileIds || [],
        artifact_ids: artifactIds || [],
        // Pass the full Attachment objects (id + name + source) so the backend can
        // include them in the real-time dev_message WebSocket event, letting
        // collaborators / viewers see attachment chips without a page reload.
        attachments: attachments || [],
        thread_id: threadId,
        model_preference: model || ModelPreference.MEDIUM,
        settings: effectiveSettings,
      });

      if (response.data.status === 'started') {
        // Use the NEW thread ID returned by the backend
        const newThreadId = response.data.thread_id;

        // Only call selectThread if the thread ID actually changed
        // If same thread, we already have WebSocket connected and don't want to reload messages
        if (newThreadId !== threadId) {
          setIsInitialLoad(true);
          await devAgent.selectThread(newThreadId);
        }
      } else if (response.data.status === 'queued') {
        // The backend queued this message because the project is busy
        // 1. Remove the optimistic messages from the chat since it hasn't started yet
        devAgent.setMessages((prev: Message[]) => prev.filter(m => m.id !== userMessage.id && m.id !== 'processing'));
        // Do NOT set isStreaming/isLoading to false here. If it was queued, it means another job is actively running!
        
        // 2. Add it to the queue banner
        const optimisticJob = {
          job_id: response.data.job_id || `optimistic-${Date.now()}`,
          thread_id: response.data.thread_id || devAgent.currentThreadId,
          status: 'QUEUED' as const,
          position: response.data.position,
          user_message: message,
          attachments: attachments || [],
        };
        promptQueue.addOptimisticJob(optimisticJob as any);
      } else {
        console.error('Unexpected response status:', response.data.status);
      }

    } catch (error) {
      console.error('Enhanced workflow failed:', error);
      // Reset streaming state on error only
      devAgent.setIsStreaming(false);
      devAgent.setIsLoading(false);
    }
    // Note: Don't reset streaming in finally block - the WebSocket events will handle that
    // when the task actually completes (task_complete event)
  };

  // ── Queue Handlers ────────────────────────────────────────────────────────

  /**
   * Called when the user clicks "Queue Message" while the agent is running.
   * Posts to /chat/start which will return status='queued' and adds an
   * optimistic card to the queue banner.
   */
  const handleQueueMessage = async (
    msgText: string,
    attachments?: Attachment[],
    selectedModel?: ModelPreference,
    settings?: Record<string, boolean>
  ) => {
    if (!projectId || !devAgent.currentThreadId) return;

    const imageIds = attachments?.filter(a => a.source === 'image').map(a => a.id);
    const fileIds = attachments?.filter(a => a.source === 'file' || a.source === 'cab_markdown').map(a => a.id);
    const artifactIds = attachments?.filter(a => a.source === 'artifact').map(a => a.id);

    try {
      // Route by interaction mode exactly like the send path, so /ask/start stamps
      // ask_mode (and viewers with only Ask permission don't 403 on /chat/start).
      const startPath = interactionMode === InteractionMode.ASK ? 'ask' : 'chat';
      // Ask mode is read-only — don't attach Agent settings to an Ask message.
      const effectiveSettings = interactionMode === InteractionMode.ASK ? {} : (settings ?? agentSettings);
      // Serialize ticket @-references into {{ticket:id|label}} tokens (same as the
      // send path) so the backend can resolve them and the reference survives edits.
      const outgoing = selectedTicketTags.length
        ? serializeTicketTagsIntoContent(msgText, selectedTicketTags)
        : msgText;
      const response = await api.post(`/dev-agent/projects/${projectId}/${startPath}/start`, {
        content: outgoing,
        image_ids: imageIds || [],
        file_ids: fileIds || [],
        artifact_ids: artifactIds || [],
        attachments: attachments || [],
        thread_id: devAgent.currentThreadId,
        model_preference: selectedModel || ModelPreference.MEDIUM,
        settings: effectiveSettings,
      });

      if (response.data.status === 'queued') {
        // Add optimistic job card so the banner appears immediately without
        // waiting for a server round-trip. DO NOT call refresh() here — doing
        // so can overwrite the optimistic state with stale data if the initial-
        // mount refresh lock is still held, making the banner flash and vanish.
        // WebSocket events (queue_job_started / queue_position_update) will
        // trigger the next real refresh at the right time.
        const optimisticJob = {
          job_id: response.data.job_id || `optimistic-${Date.now()}`,
          thread_id: response.data.thread_id || devAgent.currentThreadId,
          status: 'QUEUED' as const,
          position: response.data.position,
          user_message: outgoing,
          attachments: attachments || [],
          model_preference: selectedModel || ModelPreference.MEDIUM,
          interaction_mode: interactionMode,
          settings: effectiveSettings,
          created_at: new Date().toISOString(),
          // Show the author's avatar immediately; the server refresh later confirms it.
          user_info: user
            ? { id: user.id, email: user.email, username: user.username, firstName: user.firstName, lastName: user.lastName }
            : undefined,
        };
        promptQueue.addOptimisticJob(optimisticJob as any);
        setMessage(''); // Clear input after queueing
      }
    } catch (error: any) {
      console.error('[Queue] Failed to queue message:', error);
    }
  };


  /**
   * Called when the user clicks "Edit" on a queued job card.
   * Loads the job's message text into the ChatInput.
   */
  const handleEditJob = async (jobId: string) => {
    // If another job is already in edit mode, cancel it first.
    // Keep preEditModel/preEditSettings intact — they hold the user's original
    // preferences from before any editing started in this session.
    if (editingQueueJobId && editingQueueJobId !== jobId) {
      await promptQueue.cancelEdit(editingQueueJobId);
      setMessage('');
      setEditingQueueJobId(null);
    }

    const job = await promptQueue.startEditing(jobId);
    if (job) {
      // Capture the user's own model/settings the FIRST time we enter edit mode
      // (not when switching between jobs, so we preserve the original pre-edit state).
      if (!editingQueueJobId) {
        preEditModelRef.current = modelRef.current;
        preEditSettingsRef.current = agentSettingsRef.current;
        preEditInteractionModeRef.current = interactionModeRef.current;
      }

      // Load the raw stored message (may contain {{ticket|epic:...}} tokens).
      // ChatInput self-hydrates the chips from these tokens on edit entry and
      // reports them back via onTagsChange, so a re-save can re-serialize them.
      setMessage(job.user_message);

      // Load the job's model and settings into the UI so the user sees exactly
      // what was originally queued. Use the raw setters to avoid overwriting the
      // user's localStorage preferences (those are restored on save/cancel).
      if (job.model_preference) {
        setModelState(job.model_preference as ModelPreference);
      }
      if (job.settings) {
        setAgentSettingsState(job.settings);
      }
      if (job.interaction_mode) {
        setInteractionModeState(job.interaction_mode as InteractionMode);
      }

      setEditingQueueJobId(job.job_id);
    }
  };

  /** Restore the user's model/settings that were active before editing started. */
  const restorePreEditPreferences = () => {
    if (preEditModelRef.current !== null) {
      setModelState(preEditModelRef.current);
      preEditModelRef.current = null;
    }
    if (preEditSettingsRef.current !== null) {
      setAgentSettingsState(preEditSettingsRef.current);
      preEditSettingsRef.current = null;
    }
    if (preEditInteractionModeRef.current !== null) {
      setInteractionModeState(preEditInteractionModeRef.current);
      preEditInteractionModeRef.current = null;
    }
    // Drop the edited job's ticket tags so they don't bleed into the next message.
    setSelectedTicketTags([]);
  };

  /**
   * Called when the user clicks "Save Changes" in the ChatInput edit mode.
   */
  const handleSaveEdit = async (
    newText: string,
    _attachments?: Attachment[],
    selectedModel?: ModelPreference,
    selectedSettings?: Record<string, boolean>
  ) => {
    if (!editingQueueJobId) return;

    const imageIds = _attachments?.filter(a => a.source === 'image').map(a => a.id);
    const fileIds = _attachments?.filter(a => a.source === 'file' || a.source === 'cab_markdown').map(a => a.id);
    const artifactIds = _attachments?.filter(a => a.source === 'artifact').map(a => a.id);

    // Ask mode is read-only — don't persist Agent settings on an Ask message.
    const effectiveSettings = interactionMode === InteractionMode.ASK ? {} : (selectedSettings ?? agentSettings);

    // Re-serialize ticket @-references into {{ticket:id|label}} tokens so the edited
    // message keeps its ticket links (otherwise it degrades to a plain string).
    const serializedText = selectedTicketTags.length
      ? serializeTicketTagsIntoContent(newText, selectedTicketTags)
      : newText;

    await promptQueue.saveEdit(
      editingQueueJobId,
      serializedText,
      effectiveSettings,
      selectedModel ?? model,
      fileIds,
      artifactIds,
      imageIds,
      _attachments,
      interactionMode
    );
    setMessage('');
    setEditingQueueJobId(null);
    // Restore the user's own model/settings after the edit is committed.
    restorePreEditPreferences();
  };

  /**
   * Called when the user clicks "Cancel Edit" in the ChatInput edit mode.
   */
  const handleCancelEdit = async () => {
    if (!editingQueueJobId) return;
    await promptQueue.cancelEdit(editingQueueJobId);
    setMessage('');
    setEditingQueueJobId(null);
    // Restore the user's own model/settings — nothing was saved.
    restorePreEditPreferences();
  };

  // ─────────────────────────────────────────────────────────────────────────

  // Brainstorm message handler (Design Mode)

  const handleBrainstormMessage = async (threadId: string, message: string, attachments?: Attachment[], model?: ModelPreference) => {
    const imageIds = attachments?.filter(attachment => attachment.source === 'image').map(attachment => attachment.id);
    const fileIds = attachments?.filter(attachment => attachment.source === 'file' || attachment.source === 'cab_markdown').map(attachment => attachment.id);
    const artifactIds = attachments?.filter(attachment => attachment.source === 'artifact').map(attachment => attachment.id);
    const fileNames = attachments?.map(attachment => attachment.name);

    // Add user message to UI immediately (optimistic update)
    const userMessage: Message = {
      id: generateMessageId('user'),
      role: 'user',
      content: message,
      timestamp: new Date(),
      fileIds: fileIds || [],
      artifactIds: artifactIds || [],
      imageIds: imageIds || [],
      fileNames: fileNames || [],
      attachments: attachments || []  // Include attachments for immediate display
    };

   setBrainstormMessages(prev => [...prev, userMessage]);

    // Create unique assistant message ID for this response
    const assistantMessageId = generateMessageId('assistant');

    // Add thinking message
    const assistantMessage: Message = {
      id: assistantMessageId,
      role: 'assistant',
      content: '',
      timestamp: new Date(),
      isThinking: true,
      thinkingMessage: 'Designing...'
    };

    // Remove any existing thinking message and add new one
    setBrainstormMessages(prev => {
      const withoutThinking = prev.filter(msg => msg.id !== 'agent-thinking' && !msg.isThinking);
      return [...withoutThinking, assistantMessage];
    });

    // Create AbortController for this request
    const abortController = new AbortController();
    setBrainstormAbortController(abortController);

    try {
      const response = await authenticatedFetch(`${getApiBaseUrl()}/chat/projects/${projectId}/threads/${threadId}/stream`, {
        method: 'POST',
        body: JSON.stringify({
          content: message,
          file_ids: fileIds || [],
          artifact_ids: artifactIds || [],
          image_ids: imageIds || [],
          limit_history: false,
          history_count: 20,
          model_preference: model,
        }),
        signal: abortController.signal
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      if (!response.body) {
        throw new Error('ReadableStream not supported');
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let assistantContent = '';
      let firstChunkReceived = false;

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value, { stream: true });
        const lines = chunk.split('\n');

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));

              if (data.type === 'error') {
                console.error('❌ Chat error:', data);
                setBrainstormMessages(prev => {
                  const filtered = prev.filter(msg => msg.id !== assistantMessageId);

                  // Extract error message with robust fallbacks
                  // Check for error_user_message first (structured error from backend)
                  let errorMessage = 'An error occurred. Please try again.';
                  if (data.error_user_message && data.error_user_message.message) {
                    errorMessage = data.error_user_message.message;
                  } else if (data.data && data.data.message) {
                    errorMessage = data.data.message;
                  } else if (data.message) {
                    errorMessage = data.message;
                  }

                  const errorMsg: Message = {
                    id: generateMessageId('error'),
                    role: 'assistant',
                    content: errorMessage,
                    timestamp: new Date(),
                    isThinking: false
                  };
                  return [...filtered, errorMsg];
                });
                setBrainstormIsStreaming(false);
                setBrainstormIsLoading(false);
                return;
              }

              if (data.type === 'content') {
                assistantContent += data.content;

                if (!firstChunkReceived) {
                  firstChunkReceived = true;
                  setBrainstormIsLoading(false);
                }

                setBrainstormMessages(prev => {
                  const updated = prev.map(msg =>
                    msg.id === assistantMessageId
                      ? {
                        ...msg,
                        content: assistantContent,
                        isThinking: true,
                        thinkingMessage: 'Designing...',
                        isStreaming: true
                      }
                      : msg
                  );
                  return updated;
                });
              } else if (data.type === 'token_usage') {
                console.log('💰 Brainstorm token usage received:', data.data);
                setBrainstormMessages(prev => {
                  const updated = prev.map(msg =>
                    msg.id === assistantMessageId
                      ? {
                        ...msg,
                        tokenUsage: {
                          inputTokens: data.data.input_tokens,
                          outputTokens: data.data.output_tokens,
                          totalTokens: data.data.total_tokens,
                          cacheCreationInputTokens: data.data.cache_creation_input_tokens,
                          cacheReadInputTokens: data.data.cache_read_input_tokens,
                          serviceTier: data.data.service_tier,
                          costs: data.data.costs,
                          model: data.data.model
                        },
                        hasTokenUsage: true,
                        // Add brainstorm timing if available
                        brainstormTiming: data.data.brainstorm_timing
                      }
                      : msg
                  );
                  return updated;
                });
              } else if (data.type === 'conversation_complete' || data.type === 'done') {
                console.log('✅ Brainstorm conversation completed', data);
                setBrainstormIsStreaming(false);
                setBrainstormIsLoading(false);
                fetchProjectCost();

                // Update message ID if provided (replace temporary ID with database ID)
                const realMessageId = data.message_id;
                if (realMessageId) {
                  console.log(`📨 Updating brainstorm message ID from ${assistantMessageId} to ${realMessageId}`);
                }

                setBrainstormMessages(prev => {
                  const updated = prev.map(msg =>
                    msg.id === assistantMessageId
                      ? {
                        ...msg,
                        id: realMessageId || msg.id,  // Update ID if provided
                        isThinking: false,
                        thinkingMessage: undefined,
                        isStreaming: false
                      }
                      : msg
                  );
                  return updated;
                });
                break;
              }
            } catch (e) {
              console.error('Error parsing streaming chunk:', e);
            }
          }
        }
      }
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') {
        console.log('Brainstorm request was aborted by user');
      } else {
        console.error('Error in brainstorm chat:', error);
        
        // 🔄 CONNECTION LOST - Start polling for background streaming
        console.log('🔄 Connection lost, starting polling for background streaming...');
        
        // Show "Designing..." indicator
        setBrainstormMessages(prev => {
          const withoutThinking = prev.filter(msg => msg.id !== assistantMessageId && !msg.isThinking);
          const thinkingMessage: Message = {
            id: assistantMessageId,
            role: 'assistant',
            content: '',
            timestamp: new Date(),
            isThinking: true,
            thinkingMessage: 'Veylo Agent is designing...'
          };
          return [...withoutThinking, thinkingMessage];
        });
        
        // Start polling
        setTimeout(() => checkStreamingStatus(threadId), 5000);
      }
      setBrainstormIsStreaming(false);
      setBrainstormIsLoading(false);
    } finally {
      setBrainstormAbortController(null);
    }
  };

  const handleStopStreaming = async () => {
    console.log('🛑 Stopping conversation...');

    if (mode === ChatMode.DESIGN) {
      // Stop brainstorm stream
      if (brainstormAbortController) {
        brainstormAbortController.abort();
        setBrainstormAbortController(null);
      }

      if (brainstormThreadId && projectId) {
        try {
          await api.post(`/chat/projects/${projectId}/threads/${brainstormThreadId}/stop`);

          // Reload messages to get the stop message from database
          console.log('📥 Reloading brainstorm messages after stop...');
          await loadBrainstormThreadMessages(brainstormThreadId);
        } catch (error) {
          console.error('❌ Failed to stop brainstorm stream:', error);
        }
      }

      setBrainstormIsStreaming(false);
      setBrainstormIsLoading(false);
    } else {
      // Stop development conversation
      await devAgent.stopConversation();
    }

    console.log('✅ Conversation stopped');
  };

  const handleArtifactClick = (artifact: Artifact) => {
    // Store the artifact identifier using artifactId-messageId to track which artifact is selected
    const artifactId = `${artifact.identifier || (artifact as any).id || (artifact as any)._id || ''}-${(artifact as any).messageId || ''}`;
    selectedArtifactIdRef.current = artifactId;

    // Create a stable copy of the artifact to prevent re-renders when messages update
    setSelectedArtifact({ ...artifact });
    setSelectedKnowledgeDoc(null);
    setShowDocumentsPanel(false);
    setShowCodebasePanel(false);
  };

  const handleCloseArtifactPreview = () => {
    setIsArtifactPanelClosing(true);
    selectedArtifactIdRef.current = null;
    setTimeout(() => {
      setSelectedArtifact(null);
      setIsArtifactPanelClosing(false);
    }, 250); // Match animation duration
  };

  const handleKnowledgeDocClick = (doc: KnowledgeDocPanelData) => {
    // Mirror the artifact panel: one preview at a time, and close the side panels.
    setSelectedKnowledgeDoc({ ...doc });
    setSelectedArtifact(null);
    setShowDocumentsPanel(false);
    setShowCodebasePanel(false);
  };

  const handleCloseKnowledgeDocPreview = () => {
    setIsKnowledgeDocPanelClosing(true);
    setTimeout(() => {
      setSelectedKnowledgeDoc(null);
      setIsKnowledgeDocPanelClosing(false);
    }, 250); // Match animation duration
  };

  const loadAllThreads = async () => {
    if (!projectId) return;
    try {
      const response = await api.get(`/dev-agent/projects/${projectId}/threads/`);
      console.log('📊 [ChatPage] Threads loaded:', response.data);
      setAllThreads(response.data);
      return response.data;
    } catch (err) {
      console.error('Error loading all threads:', err);
      setAllThreads([]);
    }
  };

  const loadProject = async () => {
    if (!projectId) return;

    try {
      setLoadingProject(true);
      const response = await api.get(`/projects/${projectId}`);

      if (response.data.type === 'development') {
        setCurrentProject(response.data);
      } else {
        // Transform brainstorm project to DevProject format
        setCurrentProject({
          id: response.data.id,
          name: response.data.title,
          description: response.data.description,
          created_at: response.data.created_at,
          updated_at: response.data.created_at,
          directory_path: '',
          user_id: response.data.user_id,
          permissions: response.data.permissions,
          prompt_version: response.data.prompt_version,
          custom_domain: response.data.custom_domain,
        });
      }
      fetchProjectCost();
      // Fetch project cost
    } catch (err) {
      console.error('Error loading project:', err);
    } finally {
      setLoadingProject(false);
    }
  };

  const fetchProjectCost = async () => {
    if (!projectId) return;

    try {
      console.log('📊 [API] Fetching updated project stats for:', projectId);
      const response = await api.get(`/projects/${projectId}/stats`);
      const totalCost = parseFloat(response.data.total_cost) || 0;
      const totalDuration = parseInt(response.data.total_duration_ms) || 0;
      console.log('📊 [API] Updated project cost received:', totalCost, 'previous:', projectCost);
      setProjectCost(totalCost);
      setProjectDuration(totalDuration);

      console.log('📊 [API] ✅ Project cost updated successfully!');
    } catch (err) {
      console.error('📊 [API] ❌ Failed to fetch project cost:', err);
      setProjectCost(0);
      setProjectDuration(0);
    }
  };

  useEffect(() => {
    loadProject();
    loadAllThreads();
  }, [projectId]);

  useEffect(() => {
    if (projectId && mode === ChatMode.BUILD && allThreads.length > 0 && allThreads[0]?.id) {
      // Only select thread if not already selected
      if (devAgent.currentThreadId !== allThreads[0].id) {
        console.log('📊 [ChatPage] Selecting thread (connecting WebSocket):', allThreads[0]);
        // Show skeleton when switching threads
        setIsInitialLoad(true);
        devAgent.selectThread(allThreads[0].id);
      }
    } else if (allThreads.length === 0) {
      // If no threads exist, hide the skeleton
      console.log('📊 [ChatPage] No threads available, hiding skeleton');
      setIsInitialLoad(false);
    }
  }, [projectId, mode, allThreads]);

  // Track the previous thread ID to detect thread changes
  const prevThreadIdRef = useRef<string | undefined>();
  
  // Track when messages are loaded to hide skeleton
  useEffect(() => {
    // Hide skeleton when messages are loaded for development mode
    // Note: Brainstorm mode handles its own loading state in loadBrainstormThreadMessages
    
    if (isBrainstormThread) {
      // Brainstorm mode handles its own skeleton state
      return;
    }
    
    // Detect thread change
    const threadChanged = prevThreadIdRef.current !== currentThreadId;
    prevThreadIdRef.current = currentThreadId;
    
    if (!currentThreadId) {
      // No thread selected, hide skeleton
      setIsInitialLoad(false);
      return;
    }
    
    // If thread just changed, isInitialLoad should already be true from the thread selection code
    // We'll hide it only when messages are loaded
    
    // Hide skeleton when:
    // 1. We have messages loaded (messages.length > 0), OR
    // 2. Thread didn't just change AND not loading (empty thread case)
    if (messages.length > 0) {
      setIsInitialLoad(false);
    } else if (!threadChanged && !isLoading) {
      // Thread didn't just change and we're not loading, so this is an empty thread
      setIsInitialLoad(false);
    }
  }, [messages.length, currentThreadId, isLoading, isBrainstormThread]);

  // Smart auto-scroll - only scroll if user hasn't manually scrolled up
  useEffect(() => {
    // Auto-scroll only applies to the live view: a historical window's bottom
    // is not the thread's latest, and while the (read-only) segment view is
    // shown, background streaming must not yank its reading position
    if (isWindowedView || isViewingOriginalChat) return;
    // Only auto-scroll if user hasn't manually scrolled up
    if (!hasUserScrolledUp) {
      if (messages.length > 0) {
        const lastMessage = messages[messages.length - 1];
        
        // If it's a new message, scroll to its top
        if (lastMessage.id !== lastScrolledMessageIdRef.current) {
          const msgElement = document.getElementById(`message-${lastMessage.id}`);
          if (msgElement) {
            msgElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
            lastScrolledMessageIdRef.current = lastMessage.id;
            return;
          }
        }
      } else {
        // For empty state or initial load without messages
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      }
    }
  }, [messages, hasUserScrolledUp, isWindowedView, isViewingOriginalChat]);

  const handleModeChange = (newMode: ChatMode) => {
    setMode(newMode);
    
    // Update model preference when mode changes (project-specific)
    if (projectId) {
      const storageKey = `model_preference_${projectId}`;
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        try {
          const parsed = JSON.parse(stored);
          const modeKey = newMode === ChatMode.DESIGN ? 'design' : 'build';
          const storedModel = parsed[modeKey] || ModelPreference.MEDIUM;
          setModelState(storedModel);
        } catch {
          setModelState(ModelPreference.MEDIUM);
        }
      }
    }
    
    if (!projectId) {
      navigate(`/chat/${projectId}/${newMode.toLowerCase()}`);
      return;
    }

    if (newMode === ChatMode.CAB) {
      saveProjectPosition(projectId, 'cab', null);
      navigate(`/chat/${projectId}/cab`);
    } else if (newMode === ChatMode.TICKETS) {
      saveProjectPosition(projectId, 'tickets', null);
      navigate(`/chat/${projectId}/tickets`);
    } else if (newMode === ChatMode.DESIGN) {
      // Switching to Design mode - restore last thread from localStorage
      const savedPosition = getProjectPosition(projectId);
      const threadIdToNavigate = savedPosition?.designThreadId;
      
      // Save current mode and preserve/update designThreadId
      saveProjectPosition(projectId, 'design', threadIdToNavigate || null);
      
      if (threadIdToNavigate) {
        navigate(`/chat/${projectId}/design/${threadIdToNavigate}`);
      } else {
        // No saved thread, navigate without threadId - RequirementGroups will auto-select first
        navigate(`/chat/${projectId}/design`);
      }
    } else {
      saveProjectPosition(projectId, 'build', null);
      navigate(`/chat/${projectId}/build`);
    }
  };

  // Click a ticket @-reference chip in a Build-mode message → open that ticket
  // on the Tickets tab (TicketsPage reads the ?ticket= query param).
  const handleTicketTagClick = useCallback((ticketId: string) => {
    if (!projectId) return;
    setMode(ChatMode.TICKETS);
    saveProjectPosition(projectId, 'tickets', null);
    navigate(`/chat/${projectId}/tickets?ticket=${encodeURIComponent(ticketId)}`);
  }, [projectId, navigate]);

  // Click an epic @-reference chip in a Build-mode message → open the Epics
  // (build-order) view on the Tickets tab with that epic expanded + scrolled to
  // (EpicsView reads the ?epic= query param).
  const handleEpicTagClick = useCallback((epicId: string) => {
    if (!projectId) return;
    setMode(ChatMode.TICKETS);
    saveProjectPosition(projectId, 'tickets', null);
    navigate(`/chat/${projectId}/tickets?view=epics&epic=${encodeURIComponent(epicId)}`);
  }, [projectId, navigate]);

  // Render mode-specific action buttons
  const renderModeActionButtons = () => {
    // CAB and Tickets modes: no action buttons
    if (mode === ChatMode.CAB || mode === ChatMode.TICKETS) {
      return null;
    }

    const filesButton = (
      <Button
        label={<Icon name={IconName.Document} size='sm' className={`${showDocumentsPanel ? 'text-text-primary' : 'text-fg-secondary'}`} inheritColor />}
        variant='ghost'
        size='small'
        title='Files'
        className={showDocumentsPanel ? '!bg-bg-quaternary !rounded-full !p-bg-v2-lg' : '!rounded-full !p-bg-v2-lg'}
        onClick={() => handlePanelToggle('documents')}
      />
    );

    // A guest reads the CAB, chat history and artifacts; Files is the only tool they get.
    if (isGuest) {
      return filesButton;
    }

    // Design and Build modes: show same buttons for both
    return (
      <>
        {filesButton}
        <Button
          label={<Icon name={IconName.DesignSystem} size='sm' className={`${showDesignSystemPanel ? 'text-text-primary' : 'text-fg-secondary'}`} inheritColor />} 
          title='Design System' 
          variant='ghost' 
          size='small'
          className='!rounded-full !p-bg-v2-lg'
          onClick={toggleDesignSystemPanel}
        />
        <Button
          label={<Icon name={IconName.Link} size='sm' className={`${showUrlsModal ? 'text-text-primary' : 'text-fg-secondary'}`} inheritColor />}
          title='URLs'
          variant='ghost'
          size='small'
          className='!rounded-full !p-bg-v2-lg'
          onClick={() => setShowUrlsModal(true)}
        />
        <Button
          label={<Icon name={IconName.PreviewUrl} size='sm' className={`${showPreviewPanel ? 'text-text-primary' : 'text-fg-secondary'}`} inheritColor />}
          title='Preview'
          variant='ghost'
          size='small'
          className={showPreviewPanel ? '!bg-bg-quaternary !rounded-full !p-bg-v2-lg' : '!rounded-full !p-bg-v2-lg'}
          onClick={() => handlePanelToggle('preview')}
        />
        <Button
          label={<Icon name={IconName.Code} size='sm' className={`${showCodebasePanel ? 'text-text-primary' : 'text-fg-secondary'}`} inheritColor />}
          variant='ghost'
          size='small'
          title='Codebase'
          className={showCodebasePanel ? '!bg-bg-quaternary !rounded-full !p-bg-v2-lg' : '!rounded-full !p-bg-v2-lg'}
          onClick={() => handlePanelToggle('codebase')}
        />
        <Button
          label={<Icon name={IconName.DataModelPreview} size='sm' className={`${showDatabasePanel ? 'text-text-primary' : 'text-fg-secondary'}`} inheritColor />}
          variant='ghost'
          size='small'
          title='Database'
          className={showDatabasePanel ? '!bg-bg-quaternary !rounded-full !p-bg-v2-lg' : '!rounded-full !p-bg-v2-lg'}
          onClick={() => handlePanelToggle('database')}
        />
        <Button
          label={<Icon name={IconName.Credentials} size='sm' className={`${showCredentialsModal ? 'text-text-primary' : 'text-fg-secondary'}`} inheritColor />}
          title='Credentials'
          variant='ghost'
          size='small'
          className='!rounded-full !p-bg-v2-lg'
          onClick={() => setShowCredentialsModal(true)}
        />
        <Button
          label={<Icon name={IconName.McpServer} size='sm' className={`${showMCPServersModal ? 'text-text-primary' : 'text-fg-secondary'}`} inheritColor />}
          title='MCP Servers'
          variant='ghost'
          size='small'
          className='!rounded-full !p-bg-v2-lg'
          onClick={() => setShowMCPServersModal(true)}
        />
        {supportsEnvironments && (
          <Button
            label={<Icon name={IconName.Environment} size='sm' className={`${showEnvironmentsPanel ? 'text-text-primary' : 'text-fg-secondary'}`} inheritColor />}
            title='Environments'
            variant='ghost'
            size='small'
            className='!rounded-full !p-bg-v2-lg'
            onClick={() => setShowEnvironmentsPanel(true)}
          />
        )}
        {mode === ChatMode.BUILD && (
          <Button
            label={<ClipboardList className={`w-4 h-4 ${showTestMetadataModal ? 'text-text-primary' : 'text-fg-secondary'}`} />}
            title='Testing Context'
            variant='ghost'
            size='small'
            className={showTestMetadataModal ? '!bg-bg-quaternary !rounded-full !p-bg-v2-lg' : '!rounded-full !p-bg-v2-lg'}
            onClick={() => setShowTestMetadataModal(true)}
          />
        )}
        <AppLifecycleControl projectId={projectId} permissions={currentProject?.permissions} />
      </>
    );
  };

  const handleCloseDesignSystemPanel = () => {
    setIsDesignSystemPanelClosing(true);
    setTimeout(() => {
      setShowDesignSystemPanel(false);
      setIsDesignSystemPanelClosing(false);
    }, 300); // Match animation duration (0.3s)
  };

  const toggleDesignSystemPanel = () => {
    if (showDesignSystemPanel) {
      handleCloseDesignSystemPanel();
    } else {
      const hasOpenPanels = showDocumentsPanel || showCodebasePanel || showPreviewPanel || showDatabasePanel || selectedArtifact || selectedKnowledgeDoc;

      if (showDocumentsPanel) {
        handleCloseDocumentsPanel();
      }
      if (showCodebasePanel) {
        handleCloseCodebasePanel();
      }
      if (showPreviewPanel) {
        handleClosePreviewPanel();
      }
      if (showDatabasePanel) {
        handleCloseDatabasePanel();
      }
      if (selectedArtifact) {
        handleCloseArtifactPreview();
      }
      if (selectedKnowledgeDoc) {
        handleCloseKnowledgeDocPreview();
      }

      if (hasOpenPanels) {
        setTimeout(() => {
          setShowDesignSystemPanel(true);
        }, 250);
      } else {
        setShowDesignSystemPanel(true);
      }
    }
  };

  const handleShareArtifactClick = (artifact: Artifact) => {
    // Set the selected artifact and open the share panel
    setSelectedArtifactToShare(artifact);
    setShowArtifactSharePannel(true);
  };

  const handleToggleArtifactShare = async (artifact: Artifact) => {
    try{
      if (!projectId || !artifact.identifier) return;
      const response = await authenticatedFetch(`/api/projects/${projectId}/artifacts/${artifact.identifier}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            readonly_sharable_link_enabled: artifact.readonly_sharable_link_enabled,
          }),
        }
      );
      if (!response.ok) {
        throw new Error('Failed to share artifact');
      }
    } catch (error) {
      console.error('Failed to share artifact:', error);
      throw error;
    }
  };

  const handleShareKnowledgeDocClick = (doc: KnowledgeDocRecord) => {
    setSelectedKnowledgeDocToShare(doc);
    setShowKnowledgeDocSharePannel(true);
  };

  const handleToggleKnowledgeDocShare = async (doc: KnowledgeDocRecord) => {
    try {
      const docId = doc._id || doc.id;
      if (!projectId || !docId) return;
      const response = await authenticatedFetch(`/api/projects/${projectId}/knowledge-docs/${docId}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            readonly_sharable_link_enabled: doc.readonly_sharable_link_enabled,
          }),
        }
      );
      if (!response.ok) {
        throw new Error('Failed to share knowledge document');
      }
    } catch (error) {
      console.error('Failed to share knowledge document:', error);
      throw error;
    }
  };

  const handleShareTestRecordingClick = async (info: TestRecordingShareInfo) => {
    // Fetch the current shareable-link state before opening the panel
    let initialEnabled = false;
    try {
      const details = await getRecordingDetails(info.projectId, info.recordingId);
      initialEnabled = details.readonly_sharable_link_enabled;
    } catch (error) {
      console.error('Failed to load recording share state:', error);
    }
    setTestRecordingToShare({ ...info, initialEnabled });
    setShowTestRecordingSharePannel(true);
  };

  const handleToggleTestRecordingShare = async (enabled: boolean) => {
    if (!testRecordingToShare) return;
    await updateRecordingShareState(testRecordingToShare.projectId, testRecordingToShare.recordingId, enabled);
  };

  return (
    <div className='flex flex-col h-screen'>
      <Navbar breadcrumbs={[{ label: currentProject?.name || '' }]}
        user={user}
        projectCostDisplay={showCost ? `$${formatCostValue(projectCost)}` : undefined}
        projectDurationDisplay={showDuration ? formatLatency(projectDuration) : undefined}
        onHomeClick={() => {
          navigate('/dashboard');
        }}
        projectId={projectId}
        currentMode={mode === ChatMode.CAB ? 'cab' : mode === ChatMode.DESIGN ? 'design' : 'build'}
        actions={[
          {
            label: <Icon name={theme === 'dark' ? IconName.Sun : IconName.Moon} size='sm' variant='secondary' />,
            onClick: () => {
              setTheme(theme === 'dark' ? 'light' : 'dark');
            },
            tooltip: theme === 'dark' ? 'Light Mode' : 'Dark Mode',
          },
          {
            label: <Icon name={IconName.Bell} size='sm' variant='secondary' />,
            onClick: () => { },
            tooltip: 'Notifications',
          },
          {
            label: <Icon name={IconName.HelpCircle} size='sm' variant='secondary' />,
            onClick: () => { 
              window.open('https://docs.veylo.com/', '_blank', 'noopener,noreferrer');
            },
            tooltip: 'Help',
          }
        ]}
      />

      <Container className='px-bg-v2-3xl flex items-end justify-between' hide={showDesignSystemPanel}>
        <ModeToggleSwitch
          activeMode={mode}
          onModeChange={handleModeChange}
          showTickets
        />
        <div className='gap-bg-v2-xs flex'>
          {renderModeActionButtons()}
          {/* Manage Collaboration - visible to the owner and collaborators with invite permission */}
          {currentProject?.permissions?.includes(Permissions.InviteUserPermission) && (
            <Button
              label={<Icon name={IconName.Users} size='sm' className={`${showManageCollaborationModal ? 'text-text-primary' : 'text-fg-secondary'}`} inheritColor />}
              title='Manage Collaborators'
              variant='ghost'
              size='small'
              className='!rounded-full !p-bg-v2-lg'
              onClick={() => setShowManageCollaborationModal(true)}
            />
          )}
        </div>
      </Container>
      {/* CAB Mode — render dedicated page instead of chat UI */}
      {mode === ChatMode.CAB && (
        <Container variant='tertiary' className='flex-1 flex min-h-0 px-bg-v2-3xl py-bg-v2-xl gap-bg-v2-xl'>
            <CABPage
              onProjectCostUpdate={fetchProjectCost}
              permissions={currentProject?.permissions}
              onNavigateToTickets={() => handleModeChange(ChatMode.TICKETS)}
              enableTicketGeneration
            />
        </Container>
      )}

      {/* Tickets Mode */}
      {mode === ChatMode.TICKETS && (
        <Container variant='tertiary' className='flex-1 flex min-h-0 px-bg-v2-3xl py-bg-v2-xl gap-bg-v2-xl'>
          <TicketsPage
            permissions={currentProject?.permissions}
            currentUserId={currentProject?.user_id}
            projectPlatformType="Web"
            projectOwnerId={currentProject?.user_id}
            projectOwnerName={
              user && [user.firstName, user.lastName].filter(Boolean).join(' ') || user?.username
            }
            projectOwnerEmail={user?.email}
            onEvaluationComplete={fetchProjectCost}
          />
        </Container>
      )}

      {mode !== ChatMode.CAB && mode !== ChatMode.TICKETS && (
      <Container variant='tertiary' className='flex-1 flex min-h-0 px-bg-v2-3xl py-bg-v2-xl gap-bg-v2-xl'>

        {/* Requirement Folders - Only visible in DESIGN mode */}
        {mode === ChatMode.DESIGN && !showDesignSystemPanel && (
          <RequirementGroups
            ref={requirementGroupsRef}
            project={currentProject}
            onThreadSelect={handleThreadSelect}
            onArtifactSelect={handleArtifactClick}
            currentThreadId={currentThreadId}
            handleCloseArtifactPreview={handleCloseArtifactPreview}
            handleShareArtifact={handleShareArtifactClick}
          />
        )}

        {/* Chat Container - Takes remaining space */}
        <Container hide={showDesignSystemPanel} className='rounded-bg-v2-2xl container-shadow-md border border-border-secondary flex-1 flex flex-col min-h-0 overflow-hidden' padding='none'>
          <div className='flex items-center min-h-[60px] border-b border-border-primary' style={{ padding: '16px 17px 16px 16px' }}>
            <Text variant='text-sm' weight='semibold' className='flex items-center gap-bg-v2-sm'>
              <Icon name={IconName.Layout} size='sm' variant='secondary' />
              {mode === ChatMode.DESIGN ? 'Prototype Mode' : 'Build Mode'}
            </Text>
          </div>

          {/* Messages Container Wrapper */}
          <div className='flex-1 min-h-0 relative group/chatarea'>
          <div
            ref={chatContainerRef}
            onScroll={handleScroll}
            onWheel={chatWindow.onWheel}
            onPointerDown={chatWindow.armUserScroll}
            onTouchStart={chatWindow.armUserScroll}
            className='h-full bg-bg-primary flex flex-col items-center py-bg-v2-md overflow-y-auto modern-scrollbar-spaced'
          >
            <div className='flex flex-col gap-bg-v2-xl w-full max-w-[800px] relative'>
              {/* Skeleton Loading - Show when initially loading messages */}
              {(loadingProject || isInitialLoad) && messages.length === 0 && (
                <>
                  {/* Render 4 skeleton messages alternating: right, left, right, left */}
                  <ChatMessageSkeleton index={0} type="user" />
                  <ChatMessageSkeleton index={1} type="assistant" />
                  <ChatMessageSkeleton index={2} type="user" />
                  <ChatMessageSkeleton index={3} type="assistant" />
                </>
              )}

              {displayMessages.map((message, index) => {
                // Special handling for thinking message (check both ID and isThinking flag)
                if (message.id === 'agent-thinking' || (message.isThinking && !message.content)) {
                  return (
                    <div
                      key={message.id}
                      id={`message-${message.id}`}
                      className="animate-fade-in-up-smooth opacity-0"
                      style={{
                        animationDelay: `${Math.min(index * 0.05, 0.5)}s`,
                        animationFillMode: 'forwards'
                      }}
                    >
                      <ChatMessage
                        message={{
                          ...message,
                          content: '',
                          isThinking: true,
                          thinkingMessage: mode === ChatMode.DESIGN ? 'Designing...' : 'Thinking...',
                          timestamp: formatTimestamp(message.timestamp, {
                            month: "short",
                            day: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                            hour12: true
                          }) as any
                        }}
                        currentUser={user}
                        project={currentProject}
                        isFullWidthMessages={isFullWidthMessages}
                        isDesignMode={mode === ChatMode.DESIGN}
                        threadId={mode === ChatMode.DESIGN ? brainstormThreadId : devAgent.currentThreadId}
                        onArtifactClick={handleArtifactClick}
                        onKnowledgeDocClick={handleKnowledgeDocClick}
                        onKnowledgeDocShare={handleShareKnowledgeDocClick}
                        onTestRecordingShare={handleShareTestRecordingClick}
                        onFileClick={handleFileClick}
                        className={isFullWidthMessages ? 'pr-[16px]' : ''}
                        liveTestState={mode === ChatMode.BUILD ? devAgent.liveTestState : undefined}
                      />
                    </div>
                  );
                }

                // Check if this is an elaboration message
                const isElaborationMsg = (
                  message.messageType === 'ELABORATION' ||
                  message.messageType === 'ELABORATION_FINAL'
                );

                if (isElaborationMsg && devAgent.currentThreadId) {
                  // Use elaborationSessionId or fallback
                  const sessionId = message.elaborationSessionId || `fallback_${message.id}`;

                  // Check if this elaboration was cancelled
                  const isCancelled = devAgent.cancelledElaborations?.has(sessionId);

                  // Only show buttons for the current active elaboration session (not old ones)
                  const isCurrentActiveSession = sessionId === devAgent.elaborationSession?.session_id;
                  const shouldShowButtons = devAgent.isElaborationActive && !isCancelled && isCurrentActiveSession;

                  return (
                    <div
                      key={message.id || `message-${index}`}
                      id={`message-${message.id}`}
                      className="animate-fade-in-up-smooth opacity-0"
                      style={{
                        animationDelay: `${Math.min(index * 0.05, 0.5)}s`,
                        animationFillMode: 'forwards'
                      }}
                    >
                      <ChatMessage
                        message={{
                          ...message,
                          timestamp: formatTimestamp(message.timestamp, {
                            month: "short",
                            day: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                            hour12: true
                          }) as any
                        }}
                        currentUser={user}
                        project={currentProject}
                        // Elaboration props
                        isElaboration={true}
                        sessionId={sessionId}
                        threadId={devAgent.currentThreadId}
                        onApprove={devAgent.approveElaboration}
                        onRefine={devAgent.refineElaboration}
                        onCancel={devAgent.resetWorkflow}
                        showActionButtons={shouldShowButtons}
                        modelPreference={model}
                        agentSettings={agentSettings}
                        isFullWidthMessages={isFullWidthMessages}
                        isDesignMode={mode === ChatMode.DESIGN}
                        // Artifact props
                        onArtifactClick={handleArtifactClick}
                        onKnowledgeDocClick={handleKnowledgeDocClick}
                        onKnowledgeDocShare={handleShareKnowledgeDocClick}
                        onTestRecordingShare={handleShareTestRecordingClick}
                        onFileClick={handleFileClick}
                      />
                    </div>
                  );
                }

                // Rollback marker messages (version control)
                if (message.type === 'rollback_marker') {
                  if (message.content?.startsWith("Rolled back to")) {
                    return (
                      <div
                        key={message.id || `rollback-${index}`}
                        id={`message-${message.id}`}
                        className="animate-fade-in-up-smooth opacity-0"
                        style={{
                          animationDelay: `${Math.min(index * 0.05, 0.5)}s`,
                          animationFillMode: 'forwards'
                        }}
                      >
                        <RollbackMarker
                          message={message}
                          onViewOriginalChat={async (segmentId) => {
                            await devAgent.loadSegmentMessages(segmentId);
                            // Land at the segment's bottom (its final state);
                            // otherwise the previous scroll offset is kept
                            requestAnimationFrame(() =>
                              messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
                            );
                          }}
                        />
                      </div>
                    );
                  }
                }

                // Regular messages
                return (
                  <div
                    key={message.id || `message-${index}`}
                    id={`message-${message.id}`}
                    className={`animate-fade-in-up-smooth opacity-0 ${(isFullWidthMessages && message.role === 'user') ? 'ml-bg-v2-6xl' : ''}
                    ${(isFullWidthMessages && message.role === 'assistant') ? 'pr-bg-v2-5xl' : ''}`}
                    style={{
                      animationDelay: `${Math.min(index * 0.05, 0.5)}s`,
                      animationFillMode: 'forwards'
                    }}
                  >
                    <ChatMessage
                      message={{
                        ...message,
                        timestamp: formatTimestamp(message.timestamp, {
                          month: "short",
                          day: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                          hour12: true
                        }) as any
                      }}
                      currentUser={user}
                      project={currentProject}
                      threadId={mode === ChatMode.DESIGN ? brainstormThreadId : devAgent.currentThreadId}
                      onArtifactClick={handleArtifactClick}
                      onKnowledgeDocClick={handleKnowledgeDocClick}
                      onFileClick={handleFileClick}
                      isFullWidthMessages={isFullWidthMessages}
                      isDesignMode={mode === ChatMode.DESIGN}
                      isArtifactDetailOpen={selectedArtifact !== null || selectedKnowledgeDoc !== null}
                      isProcessing={isLoading || isSendingMessage || isStreaming || devAgent.isElaborationActive}
                      onCheckpointRevert={mode === ChatMode.BUILD ? handleInlineCheckpointRevert : undefined}
                      revertingMessageId={revertingMessageId}
                      onKnowledgeDocShare={handleShareKnowledgeDocClick}
                      onTestRecordingShare={handleShareTestRecordingClick}
                      liveTestState={mode === ChatMode.BUILD ? devAgent.liveTestState : undefined}
                      onTicketTagClick={handleTicketTagClick}
                      onEpicTagClick={handleEpicTagClick}
                    />
                  </div>
                );
              })}
              {/* Browser Live Stream Container - rendered as sticky at bottom */}

              {/* Revert status indicator */}
              {devAgent.revertStatus && (
                <div className="flex items-center gap-3 px-4 py-3 mx-4 mb-2 rounded-lg bg-bg-secondary border border-border-secondary animate-fade-in-up-smooth">
                  <svg className="animate-spin h-4 w-4 text-text-brand-primary flex-shrink-0" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  <span className="text-sm text-text-secondary">
                    {devAgent.revertStatus === 'restoring' && 'Restoring to checkpoint...'}
                    {devAgent.revertStatus === 'rebuilding' && 'Restarting application...'}
                  </span>
                </div>
              )}

              <div ref={messagesEndRef} data-messages-end />
            </div>

          </div>

            {/* Pagination indicators - overlaid siblings of the scroll container */}
            {(devIsLoadingMore || brainstormIsLoadingMore) && <ChatLoadingIndicator edge='top' />}
            {devIsLoadingNewer && <ChatLoadingIndicator edge='bottom' />}

            {/* Scroll to Bottom Button - Sibling of scroll container, inside relative wrapper */}
            {(!isAtBottom || isWindowedView || isSegmentWindowed) && (
              <div className='absolute bottom-[8px] left-1/2 -translate-x-1/2 z-10 pointer-events-none'>
                <button
                  onClick={async () => {
                    setHasUserScrolledUp(false);
                    // A historical window's loaded bottom is not the true tail
                    // — reload the latest page first, then jump to it
                    if (isSegmentWindowed && devAgent.viewingSegmentId) {
                      await devAgent.loadSegmentMessages(devAgent.viewingSegmentId);
                      requestAnimationFrame(() =>
                        messagesEndRef.current?.scrollIntoView({ behavior: 'auto' })
                      );
                    } else if (isWindowedView && devAgent.currentThreadId) {
                      await devAgent.loadMessages(devAgent.currentThreadId, true, 20);
                      requestAnimationFrame(() =>
                        messagesEndRef.current?.scrollIntoView({ behavior: 'auto' })
                      );
                    } else {
                      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
                    }
                  }}
                  className='w-[36px] h-[36px] rounded-full bg-bg-primary border border-border-secondary shadow-shadow-md flex items-center justify-center hover:bg-bg-secondary transition-colors duration-200 pointer-events-auto'
                  aria-label='Scroll to bottom'
                >
                  <Icon name={IconName.ArrowDown} size='sm' variant='secondary' />
                </button>
              </div>
            )}

            {/* Conversation Navigator - right-edge minimap of user turns (Build Mode only) */}
            {navEnabled && (
              <ChatNavigator
                items={navItems}
                onSelect={chatWindow.jumpTo}
                containerRef={chatContainerRef}
                getMessageElement={getMessageElement}
                loadedCount={displayMessages.length}
                oldestLoadedId={displayMessages[0]?.id}
                newestLoadedId={displayMessages[displayMessages.length - 1]?.id}
              />
            )}
          </div>

          {isViewingOriginalChat ? (
            /* View Original Chat — replaces chat input with resume button */
            <div className="flex justify-center py-4 px-4 border-t border-border-secondary bg-bg-primary">
              <button
                onClick={() => {
                  // The active branch's list was never touched — just land at
                  // its tail instead of the segment view's scroll offset
                  setHasUserScrolledUp(false);
                  devAgent.clearViewingSegment();
                  requestAnimationFrame(() =>
                    messagesEndRef.current?.scrollIntoView({ behavior: 'auto' })
                  );
                }}
                className="flex items-center gap-2 text-sm px-5 py-2.5 rounded-lg bg-bg-brand-solid hover:bg-bg-brand-solid-hover
                           text-text-primary-on-brand transition-colors"
              >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                Resume current chat
              </button>
            </div>
          ) : (
            /* Normal Chat Input with Queue support */
            <div className='flex flex-col bg-bg-primary'>
              {/* Queue panel + input share one centered column so the panel docks
                  flush onto the top edge of the input box. */}
              <div className='flex justify-center pt-bg-v2-lg pb-bg-v2-3xl px-bg-v2-xl'>
                <div className="w-full max-w-[800px] flex flex-col items-stretch">
                  {mode === ChatMode.BUILD && (
                    <>
                      <QueuedMessageBanner
                        jobs={promptQueue.queuedJobs}
                        onEdit={handleEditJob}
                        onResumeAll={promptQueue.resumeAll}
                        onCancel={(jobId) => {
                          if (editingQueueJobId === jobId) {
                            setMessage('');
                            setEditingQueueJobId(null);
                          }
                          promptQueue.cancelJob(jobId);
                        }}
                        editingJobId={editingQueueJobId}
                        canEditProject={canEditProject}
                        currentUserId={user?.id}
                      />
                    </>
                  )}
                <IfHasPermission
                  permissions={currentProject?.permissions}
                  requires={
                    mode === ChatMode.BUILD
                      ? (interactionMode === InteractionMode.ASK ? Permissions.AskModePermission : Permissions.UpdateProjectPermission)
                      : Permissions.UpdateProjectPermission
                  }
                  fallback={<ReadOnlyProjectNotice />}
                >
                  <ChatInput
                    className={promptQueue.queuedJobs.length > 0 && mode === ChatMode.BUILD ? '[&>div]:!rounded-t-none [&>div]:!border-t-0' : ''}
                    value={message}
                    onChange={setMessage}
                    onSend={handleSendMessage}
                    onStop={handleStopStreaming}
                    placeholder={mode === ChatMode.DESIGN && !brainstormThreadId ? 'Select a requirement folder to start chatting...' : 'Type your message...'}
                    disabled={isSendingMessage || (mode === ChatMode.DESIGN && !brainstormThreadId)}
                    isStreaming={isStreaming}
                    isSending={isSendingMessage}
                    projectId={projectId}
                    showModelSelector={true}
                    model={model}
                    onModelChange={setModel}
                    showAgentSettings={mode === ChatMode.BUILD && canEditProject}
                    enableTagChips={mode === ChatMode.BUILD}
                    onTagSearch={mode === ChatMode.BUILD ? handleTicketTagSearch : undefined}
                    onTagsChange={mode === ChatMode.BUILD ? handleTicketTagsChange : undefined}
                    tagSuggestions={ticketTagSuggestions}
                    isTagSearching={isTicketTagSearching}
                    agentSettings={agentSettings}
                    onAgentSettingsChange={setAgentSettings}
                    showInteractionMode={mode === ChatMode.BUILD}
                    interactionMode={interactionMode}
                    onInteractionModeChange={setInteractionMode}
                    interactionModeLockedToAsk={!canEditProject}
                    isQueueMode={mode === ChatMode.BUILD && (isStreaming || isLoading || devAgent.isElaborationActive)}
                    onQueue={handleQueueMessage}
                    editingJobId={editingQueueJobId}
                    editingAttachments={promptQueue.editingJob?.attachments}
                    onSaveEdit={handleSaveEdit}
                    onCancelEdit={handleCancelEdit}
                  />
                </IfHasPermission>
                </div>
              </div>
            </div>
          )}
        </Container>

        {/* Documents Panel - Shown inline when active */}
        {showDocumentsPanel && projectId && currentProject && (
          <div className={`w-[55%] flex-shrink-0 ${isDocumentsPanelClosing ? 'animate-slide-out-right' : 'animate-slide-in-right'}`}>
            <Container className='rounded-bg-v2-2xl container-shadow-md border border-border-secondary h-full flex flex-col overflow-hidden' padding={"none"}>
              <DocumentsPanel
                project={currentProject}
                onClose={handleCloseDocumentsPanel}
              />
            </Container>
          </div>
        )}

        {/* Preview Panel - Shown inline when active */}
        {showPreviewPanel && projectId && (
          <div className={`w-[55%] flex-shrink-0 ${isPreviewPanelClosing ? 'animate-slide-out-right' : 'animate-slide-in-right'}`}>
            <Container className='rounded-bg-v2-2xl container-shadow-md border border-border-secondary h-full flex flex-col overflow-hidden' padding={"none"}>
              <PreviewPanel
                key={previewRefreshKey}
                projectId={projectId}
                promptVersion={currentProject?.prompt_version}
                onClose={handleClosePreviewPanel}
              />
            </Container>
          </div>
        )}

        {/* Codebase Panel - Shown inline when active */}
        {showCodebasePanel && projectId && (
          <div className={`w-[55%] flex-shrink-0 ${isCodebasePanelClosing ? 'animate-slide-out-right' : 'animate-slide-in-right'}`}>
            <Container className='rounded-bg-v2-2xl container-shadow-md border border-border-secondary h-full flex flex-col overflow-hidden' padding={"none"}>
              <CodebasePanel
                projectId={projectId}
                onClose={handleCloseCodebasePanel}
                permissions={currentProject?.permissions}
                initialSelectedFile={initialCodebaseFile}
                onInitialFileSelected={() => setInitialCodebaseFile(null)}
                isDevelopmentInProgress={devAgent.isLoading || devAgent.isStreaming}
              />
            </Container>
          </div>
        )}

        {/* Artifact Preview Panel - Shown inline when active */}
        {selectedArtifact && (
          <div className={`w-[55%] flex-shrink-0 ${isArtifactPanelClosing ? 'animate-slide-out-right' : 'animate-slide-in-right'}`}>
            <Container className='rounded-bg-v2-2xl container-shadow-md border border-border-secondary h-full flex flex-col overflow-hidden' padding={"none"}>
              <ArtifactDetail
                artifact={selectedArtifact}
                isOpen={true}
                onClose={handleCloseArtifactPreview}
                permissions={currentProject?.permissions}
              />
            </Container>
          </div>
        )}

        {/* Knowledge Document Preview Panel - Shown inline when active */}
        {selectedKnowledgeDoc && (
          <div className={`w-[55%] flex-shrink-0 ${isKnowledgeDocPanelClosing ? 'animate-slide-out-right' : 'animate-slide-in-right'}`}>
            <Container className='rounded-bg-v2-2xl container-shadow-md border border-border-secondary h-full flex flex-col overflow-hidden' padding={"none"}>
              <KnowledgeDocDetail
                doc={selectedKnowledgeDoc}
                isOpen={true}
                onClose={handleCloseKnowledgeDocPreview}
                permissions={currentProject?.permissions}
              />
            </Container>
          </div>
        )}

        {/* Database Panel - Shown inline when active (BUILD mode only) */}
        {showDatabasePanel && projectId && (
          <div className={`w-[55%] flex-shrink-0 ${isDatabasePanelClosing ? 'animate-slide-out-right' : 'animate-slide-in-right'}`}>
            <Container className='rounded-bg-v2-2xl container-shadow-md border border-border-secondary h-full flex flex-col overflow-hidden' padding={"none"}>
              <DatabasePreviewPannel
                projectId={projectId}
                permissions={currentProject?.permissions || []}
                onClose={handleCloseDatabasePanel}
              />
            </Container>
          </div>
        )}

        {/* Design System Panel - Full width replacement */}
        {showDesignSystemPanel && projectId && (
          <div className={`flex-1 ${isDesignSystemPanelClosing ? 'animate-slide-out-down' : 'animate-slide-in-up'}`}>
          <DesignSystemPanel
            isOpen={showDesignSystemPanel}
            isClosing={isDesignSystemPanelClosing}
            project={currentProject}
            onDesignSystemChange={() => {}}
            onUnsavedChanges={() => {}}
              onClose={handleCloseDesignSystemPanel}
          />
          </div>
        )}

      </Container>
      )}

      {/* Modals */}

      {showUrlsModal && projectId && (
        <ApplicationUrlsModal
          projectId={projectId}
          onClose={() => setShowUrlsModal(false)}
        />
      )}

      {showCredentialsModal && projectId && currentProject && (
        <AuthCredentialsModal
          project={currentProject}
          onClose={() => setShowCredentialsModal(false)}
        />
      )}

      {showMCPServersModal && projectId && currentProject && (
        <MCPServersModal
          project={currentProject}
          onClose={() => setShowMCPServersModal(false)}
          isBuilding={devAgent.isLoading || devAgent.isStreaming}
        />
      )}

      {showEnvironmentsPanel && projectId && currentProject && supportsEnvironments && (
        <EnvironmentsPanel
          project={currentProject}
          onClose={() => setShowEnvironmentsPanel(false)}
          isBuilding={devAgent.isLoading || devAgent.isStreaming}
        />
      )}

      {showTestMetadataModal && projectId && devAgent.currentThreadId && (
        <TestMetadataModal
          projectId={projectId}
          threadId={devAgent.currentThreadId}
          onClose={() => setShowTestMetadataModal(false)}
        />
      )}

      {/* Credential Capture Modal - Shows when agent requests credentials during development (only for users with update permission) */}
      {devAgent.credentialCaptureRequest && mode === ChatMode.BUILD && currentProject?.permissions?.includes(Permissions.UpdateProjectPermission) && (
        <CredentialCaptureModal
          visible={!!devAgent.credentialCaptureRequest}
          requestId={devAgent.credentialCaptureRequest.requestId}
          parameters={devAgent.credentialCaptureRequest.parameters}
          envFilePath={devAgent.credentialCaptureRequest.envFilePath}
          onSubmit={devAgent.submitCapturedCredentials}
          onCancel={devAgent.clearCredentialCaptureRequest}
        />
      )}
      {selectedArtifactToShare &&
        <ArtifactSharePannel
          isOpen={showArtifactSharePannel}
          selectedArtifact={selectedArtifactToShare}
          isClosing={isArtifactSharePannelClosing}
          onClose={handleCloseArtifactSharePannel}
          onSave={handleToggleArtifactShare}
          />
      }
      {selectedKnowledgeDocToShare &&
        <KnowledgeDocSharePannel
          isOpen={showKnowledgeDocSharePannel}
          selectedDoc={selectedKnowledgeDocToShare}
          isClosing={isKnowledgeDocSharePannelClosing}
          onClose={handleCloseKnowledgeDocSharePannel}
          onSave={handleToggleKnowledgeDocShare}
          />
      }
      {testRecordingToShare &&
        <TestRecordingSharePanel
          isOpen={showTestRecordingSharePannel}
          isClosing={isTestRecordingSharePannelClosing}
          projectId={testRecordingToShare.projectId}
          recordingId={testRecordingToShare.recordingId}
          title={testRecordingToShare.title}
          initialEnabled={testRecordingToShare.initialEnabled}
          onClose={handleCloseTestRecordingSharePannel}
          onSave={handleToggleTestRecordingShare}
          />
      }

      {/* Manage Collaboration Modal */}
      {showManageCollaborationModal && currentProject && (
        <ManageCollaborationModal
          isOpen={showManageCollaborationModal}
          onClose={() => setShowManageCollaborationModal(false)}
          selectedProject={{
            id: projectId || "",
            title: currentProject.name,
            description: currentProject.description || '',
            project_type: 1, // Web project
            created_at: currentProject.created_at,
            updated_at: currentProject.updated_at,
            user_id: currentProject.user_id,
          }}
        />
      )}
    </div>
  );
};